// This file is auto-generated by @hey-api/openapi-ts

export type Action = {
  /**
   * URL to be loaded upon clicking the CTA. Make sure that the url has appropriate analytics params for tracking source, i.e. 'source' param in case of OfA deeplinks.
   */
  url: string
  /**
   * Indicates target that should be used to handle the action URL.
   */
  target: 'host' | 'sdk'
  /**
   * Action text to be displayed on the CTA.
   */
  text: Translatable
}

/**
 * Action for which text is provided by client.
 */
export type ActionNoText = {
  /**
   * URL to be loaded upon clicking the CTA. Make sure that the url has appropriate analytics params for tracking source, i.e. 'source' param in case of OfA deeplinks.
   */
  url: string
  /**
   * Indicates target that should be used to handle the action URL.
   */
  target: 'host' | 'sdk'
}

export type ActivityEntry = {
  id: string
  description: Translatable
  amount: CurrencyAmount
  timestamp: string
  boost?: number | null
  marketItem?: MarketItemReference | null
  /**
   * For coupon purchases, contains the code, instructions for using it, expiry date, and partner link. Null for other activity entries and purchases without a code. Instructions, expiry date, and partner link come from the campaign settings when activity is loaded, not when the coupon was purchased. Changes to those settings appear after the activity cache expires or is cleared.
   */
  extra?: CouponCampaignPurchaseResponse | null
  cryptoOffer?: CryptoOfferMetadata | null
  /**
   * Crypto-token earn metadata. Set only for crypto-token earn entries; null for all other activity sources.
   */
  crypto?: CryptoActivityMetadata | null
  /**
   * Legacy field no longer used by clientsdk BUT required by clientsdk during client-side deserialization. We need to keep it otherwise we'll break old clients
   */
  pointsLogId?: -1
}

export type AddPointsLogRequest = {
  amount: number
}

export type AddTargetingRequest = {
  targetingId: number
}

export type AdminLeaderboardClientConfigSchema = {
  clientType: ClientType
  enabled: boolean
  tiers: Array<AdminLeaderboardTierConfigSchema>
  minimalVersion?: string | null
}

export type AdminLeaderboardConfigSchema = {
  tokenType: CryptoTokenType
  clientTypes: Array<AdminLeaderboardClientConfigSchema>
}

export type AdminLeaderboardKeySchema = {
  key: string
  clientType: ClientType
  yearWeek: string
}

export type AdminLeaderboardOverviewSchema = {
  config: AdminLeaderboardConfigSchema
  keys: Array<AdminLeaderboardKeySchema>
}

export type AdminLeaderboardStatisticsSchema = {
  key: string
  totalMembers: number
  tiers: Array<AdminLeaderboardTierStatSchema>
}

export type AdminLeaderboardTierConfigSchema = {
  tier: number
  startRank: number
  endRank: number
  rewardPerUser: string
  totalBudget: string
}

export type AdminLeaderboardTierStatSchema = {
  tier: number
  startRank: number
  endRank: number
  memberCount: number
  totalBudget: string
  rewardPerUser: string
  top: AdminRankedMemberSchema
  bottom: AdminRankedMemberSchema
}

export type AdminLoginSchema = {
  credentials: string
}

export type AdminMeSchema = {
  email: string
  role: OperatorRole
  permissions: Array<Permission>
}

export type AdminRankedMemberSchema = {
  appInstanceId: number
  rank: number
  score: number
}

/**
 * Represents value of specific type i.e. 10 points or 5 Celo (Wei) tokens.
 */
export type Amount = {
  unit: RewardCurrency
  value: number
}

export type AppInstanceEarningsSummarySchema = {
  pointsEarned: number
  missionsCompleted: number
}

export type AppInstanceLeaderboardSchema = {
  membership: LeaderboardMembershipSchema
  rank: LeaderboardRankSchema
  eligibility: LeaderboardEligibilitySchema
}

export type AppInstanceSchema = {
  id: number
  deviceId: string
  clientType: ClientType
  countryCode: string
  operaUserId: string | null
  clientVersion: string
  sdkVersion: string | null
  timezone: string
  installationTime: string | null
  createdAt: string
  updatedAt: string
  /**
   * None indicates that it is not known.
   */
  debugHttpLoggingEnabled?: boolean | null
  internal: boolean
  celoWallet: string | null
  celoWalletBoundAt: string | null
  comment?: string | null
  userClients?: Array<unknown>
}

export type AppInstanceStateSchema = {
  id: number
  appInstanceId: number
  isLoggedIntoGoogle: boolean | null
  isDefaultBrowser: boolean | null
  isLoggedIntoHostApp: boolean | null
  isMinipayInstalled: boolean | null
  isGooglePlayAvailable: boolean | null
  diagnosticInfo: DiagnosticInfo | null
  createdAt: string
  updatedAt: string
  isFresh: boolean
}

export type AppInstanceTargetingSchema = {
  targetingId: number
  slugName: string
  appInstanceId: number
  lastSyncedAt: string | null
}

export type AppInstanceTokenSchema = {
  id: string
  appInstanceId: number
  expiresAt: string
  createdAt: string
  updatedAt: string
}

export type AppInstanceUsdtStatusResponse = {
  purchases: Array<UsdtPurchaseHistorySchema>
  walletRateLimit: UsdtWalletRateLimitStatus
}

export type AppInstancesResponse = {
  items?: Array<AppInstanceSchema>
}

export type AssignableRewardItemStats = {
  items: Array<AssignableRewardItemStatsEntry>
}

export type AssignableRewardItemStatsEntry = {
  variant: AssignableRewardTypeVariant
  amount: string
  countryCode: string
  countWinnableImmediately: number
  countWinnableInTheFuture: number
  countWon: number
  countTotal: number
}

/**
 * Holds a list of statistics based on winnable dates.
 */
export type AssignableRewardItemWinnableDateStats = {
  items: Array<AssignableRewardItemWinnableDateStatsEntry>
}

/**
 * Represents the count for a specific group on a specific winnable date.
 */
export type AssignableRewardItemWinnableDateStatsEntry = {
  countryCode: string
  variant: string
  amount: string
  winnableFromDate: string
  count: number
}

export enum AssignableRewardTypeVariant {
  BET9JA_BONUS = 'bet9ja_bonus',
  BET9JA_SPORT = 'bet9ja_sport',
  BET9JA_CASINO = 'bet9ja_casino',
  BET9JA_SIMULATE = 'bet9ja_simulate',
  BET9JA_JACKPOT = 'bet9ja_jackpot',
  POINTS = 'points',
  CUSD = 'cusd',
  CUSD_JACKPOT = 'cusd_jackpot',
  JACKPOT_CUSD = 'jackpot_cusd',
  DAPP_REWARD_CLAIM = 'dapp_reward_claim'
}

export type BalanceResponse = {
  walletAddress: string | null
  /**
   * Users' bound wallet balance
   */
  walletBalance: CurrencyAmount | null
  /**
   * Users' points balance
   */
  pointsBalance: CurrencyAmount
}

export type BalanceV2Response = {
  walletAddress: string | null
  balances: Array<CurrencyAmount>
  boost: number
}

/**
 * Colors configuration for a banner with gradient background.
 */
export type BannerColors = {
  /**
   * Indicates if the banner uses dark theme (affects text color).
   */
  isDark: boolean
  /**
   * List of color hex codes for gradient background. Colors are distributed evenly (e.g., 2 colors: 0%-100%, 3 colors: 0%-50%-100%).
   */
  background: Array<string>
}

export type BannerItem = {
  /**
   * Unique identifier (slug) for the banner. Can be used by clients to report impressions related to this banner.
   */
  id: string
  title: Translatable
  text: Translatable
  /**
   * Predefined color token for the banner (e.g., 'Purple').
   */
  colorToken?: string | null
  /**
   * Custom colors configuration for the banner.
   */
  colors?: BannerColors | null
  /**
   * Primary action for the banner (e.g., 'Redeem USDT' button).
   */
  action?: Action | null
  /**
   * Secondary info action for the banner (e.g., 'T&C apply' link).
   */
  infoAction?: Action | null
  /**
   * URL of the banner image.
   */
  image?: string | null
  /**
   * Vertical alignment of the banner image.
   */
  imageAlignment?: 'top' | 'center' | 'bottom' | null
}

export type BannersResponse = {
  items: Array<BannerItem>
}

export enum BetOption {
  WIN = 'win',
  LOSE = 'lose',
  DRAW = 'draw'
}

export type BindWalletRequest = {
  /**
   * The full EIP-4361 SIWE message string received from POST /auth/wallet-challenge.
   */
  message: string
  /**
   * EIP-191 personal_sign signature of the SIWE message by the wallet private key.
   */
  signature: string
}

export type Body_feedback = {
  screenshot?: string | null
  country_code: string
  phone_number: string
  email: string
  summary: string
  problem_area: ProblemArea
}

export type Body_upload_codes = {
  file: string
}

/**
 * Frequency used to derive claimable occurrences from a campaign start.
 */
export enum BonusCampaignFrequency {
  ONE_OFF = 'one_off',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly'
}

/**
 * Paginated campaign list, newest-first.
 */
export type BonusCampaignListResponse = {
  items: Array<BonusCampaignResponse>
  total: number
  limit: number
  offset: number
}

/**
 * Full bonus campaign detail.
 *
 * Amount fields (`rewardAmount`, `distributionLimit`) are natural-unit
 * `Decimal` values serialized as exact JSON decimal strings.
 */
export type BonusCampaignResponse = {
  id: number
  name: string
  targetingId: number
  status: string
  startsAt: string
  endsAt: string
  timezone: string
  frequency: string
  tokenType: string
  /**
   * Reward per earning, in the token's natural unit.
   */
  rewardAmount: string
  /**
   * Maximum reward amount per occurrence, in the token's natural unit.
   */
  distributionLimit: string
  activatedAt: string | null
  endedAt: string | null
  createdAt: string
  updatedAt: string
}

export type BonusClaimUnavailableApiErrorBody = {
  detail: string
  errorCode: 'bonus_claim_unavailable'
}

export enum BoostFeature {
  USER_LOGGED_IN_TO_GOOGLE = 'user_logged_in_to_google',
  IS_DEFAULT_BROWSER = 'is_default_browser',
  USER_LOGGED_IN_TO_HOST_APP = 'user_logged_in_to_host_app',
  SELF_VERIFY_COMPLETED = 'self_verify_completed'
}

export type BoostValueConfig = {
  feature: BoostFeature
  value: number
}

export type CacheEntriesResponse = {
  items: Array<CacheHashEntrySchema>
}

export type CacheHashEntrySchema = {
  key: string
  fields: Array<CacheHashFieldSchema>
  /**
   * TTL of cache key
   */
  ttl: number
}

export type CacheHashFieldSchema = {
  fieldName: string
  value: unknown
  /**
   * TTL of cache hash
   */
  ttl: number
}

export type CachedFunctionSchema = {
  identifier: string
}

export type CachedFunctionsListSchema = {
  functions: Array<CachedFunctionSchema>
}

export type CachedKeySchema = {
  key: string
}

export type CachedKeysListSchema = {
  function_identifier: string
  keys: Array<CachedKeySchema>
  /**
   * None means that there are no more pages.
   */
  next_cursor: number | null
  total_count: number
}

export type CamelModel = unknown

export type CampaignAdminSchema = {
  id: number
  partnerId: string
  clientTypes: Array<ClientType>
  countryCodes: Array<string>
  title: string
  description: string
  howToUse: string | null
  logoUrl: string | null
  claimSteps: Array<string>
  redirectUrl: string | null
  redirectButtonLabel: string | null
  pointsCost: number
  maxClaimsPerPeriod: number
  claimPeriod: ClaimPeriod
  remainingCodes: number
  validFrom: string | null
  validTo: string | null
  isPaused: boolean
  supplyMethod: SupplyMethod
  apiEndpoint: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Paginated campaign audit history, oldest-first.
 */
export type CampaignAuditLogListResponse = {
  items: Array<CampaignAuditLogResponse>
  total: number
  limit: number
  offset: number
}

/**
 * Single campaign audit log entry.
 */
export type CampaignAuditLogResponse = {
  id: number
  /**
   * ID of the affected campaign. `null` when a Draft was permanently deleted; the `details` field preserves the former ID.
   */
  campaignId: number | null
  operatorEmail: string
  action: string
  /**
   * Action-specific audit payload.
   */
  details: {
    [key: string]: unknown
  }
  createdAt: string
}

export type CampaignStatsSchema = {
  remainingCodes: number
  totalIssued: number
}

export type CancelTokenPayoutRunRequest = {
  runId: number
}

/**
 * API returning CELO token cashout specific info.
 */
export type CashoutConfigResponse = {
  minAmount: CurrencyAmountToken
  /**
   * None if MiniPay app is not needed to manage currency in MiniPay.
   */
  miniPayApp: MiniPayApp | null
  /**
   * None if exchange rate not available.
   */
  celoValueInUsd: string | null
}

export type CashoutConfigV2Response = {
  configs: Array<CashoutCurrencyConfigV2Response>
}

export type CashoutCurrencyConfigV2Response = {
  currency: CryptoTokenType
  minAmount: string
  /**
   * None if MiniPay app is not needed to manage currency in MiniPay.
   */
  miniPayApp: MiniPayApp | null
  /**
   * None if exchange rate not available.
   */
  unitValueInUsd: string | null
}

export type CashoutSuccessPayload = {
  amount: CurrencyAmountToken
  txHash: string
}

export type CashoutTxAlreadyRecordedApiErrorBody = {
  detail: string
  errorCode: 'token_cashout_tx_already_recorded'
}

export type CashoutTxAmountMismatchApiErrorBody = {
  detail: string
  errorCode: 'token_cashout_tx_amount_mismatch'
}

export type CashoutTxHasNoToAddressApiErrorBody = {
  detail: string
  errorCode: 'token_cashout_tx_has_no_to_address'
}

export type CashoutTxNotFoundApiErrorBody = {
  detail: string
  errorCode: 'token_cashout_tx_not_found'
}

export type CeloBalanceResponse = {
  /**
   * Wallet address in checksummed EIP-55 format.
   */
  address: string
  /**
   * Native CELO balance in Wei (1 CELO = 10^18 Wei). Returned as string to avoid JavaScript float precision loss — even small balances exceed JS MAX_SAFE_INTEGER (2^53 ≈ 9×10^15).
   */
  balanceWei: string
}

export type CeloConfigResponse = {
  /**
   * Celo JSON-RPC endpoint URL currently in use.
   */
  rpcUrl: string
}

export type CeloIntentActiveIntentExistsApiErrorBody = {
  detail: string
  errorCode: 'market_celo_intent_active_intent_exists'
}

export type CeloIntentFeatureDisabledApiErrorBody = {
  detail: string
  errorCode: 'market_celo_intent_feature_disabled'
}

export type CeloIntentNoWalletBoundApiErrorBody = {
  detail: string
  errorCode: 'market_celo_intent_no_wallet_bound'
}

export type CeloPurchaseIntentAccessDeniedApiErrorBody = {
  detail: string
  errorCode: 'market_celo_purchase_intent_access_denied'
}

export type CeloReceiptSchema = {
  /**
   * 0x-prefixed hex hash of the transaction this receipt belongs to.
   */
  transactionHash: string
  /**
   * Block number in which the transaction was mined.
   */
  blockNumber: number
  /**
   * Sender wallet address (checksummed EIP-55 format).
   */
  fromAddress: string
  /**
   * Recipient wallet address. null for contract deployment transactions.
   */
  toAddress: string | null
  /**
   * Execution result: 1 = success, 0 = reverted. A reverted transaction is still mined and the fee is still charged.
   */
  status: number
  /**
   * Actual computation units consumed. Transaction fee = gas_used × gas_price (gas_price from the transaction).
   */
  gasUsed: number
  /**
   * ERC-20 Transfer events decoded from this transaction's logs.
   */
  erc20Transfers?: Array<Erc20TransferSchema>
}

export type CeloTransactionResponse = {
  /**
   * Unique 0x-prefixed hex identifier of the transaction.
   */
  transactionHash: string
  /**
   * Block in which the transaction was included. null if still pending (not yet mined).
   */
  blockNumber: number | null
  /**
   * Sender wallet address (checksummed EIP-55 format).
   */
  fromAddress: string
  /**
   * Recipient wallet address. null for contract deployment transactions.
   */
  toAddress: string | null
  /**
   * Amount of native CELO transferred, in Wei (1 CELO = 10^18 Wei). Returned as string to avoid JavaScript float precision loss.
   */
  valueWei: string
  /**
   * Gas limit — maximum computation units the sender authorized for this transaction. Simple CELO transfers always cost exactly 21,000 gas; smart contract calls cost more. Safely represented as int: even the block gas limit (~130M) is far below JS MAX_SAFE_INTEGER.
   */
  gas: number
  /**
   * Price per gas unit, in Wei. Actual fee paid = gas_used × gas_price (see receipt). Typical values: 1–25 Gwei (1 Gwei = 10^9 Wei). Safely represented as int: values would need to exceed 9,000,000 Gwei to overflow JS MAX_SAFE_INTEGER.
   */
  gasPrice: number
  /**
   * Sequential transaction counter for the sender's address. Prevents replay attacks and orders concurrent transactions from the same wallet.
   */
  nonce: number
  /**
   * True if the transaction has not yet been included in a block.
   */
  isPending: boolean
  /**
   * Execution receipt, available only once the transaction is mined. null if still pending.
   */
  receipt: CeloReceiptSchema | null
}

export type CheckSignatureRequest = {
  signature: string
}

export enum ClaimPeriod {
  TOTAL = 'TOTAL',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY'
}

export type ClientStateData = {
  /**
   * Whether user is logged into Google
   */
  isLoggedIntoGoogle?: boolean | null
  /**
   * Whether client app is set as default browser
   */
  isDefaultBrowser?: boolean | null
  /**
   * Whether user is logged into host app
   */
  isLoggedIntoHostApp?: boolean | null
  /**
   * Whether ClientSDK detects standalone MiniPay
   */
  isMinipayInstalled?: boolean | null
  /**
   * Whether ClientSDK detects available Google Play
   */
  isGooglePlayAvailable?: boolean | null
  /**
   * Canonical MiniPay wallet hash
   */
  minipayWalletHash?: string | null
  /**
   * Diagnostic info reported by ClientSDK
   */
  diagnosticInfo?: DiagnosticInfo | null
}

export type ClientStateResponse = {
  /**
   * Whether user is logged into Google
   */
  isLoggedIntoGoogle?: boolean | null
  /**
   * Whether client app is set as default browser
   */
  isDefaultBrowser?: boolean | null
  /**
   * Whether user is logged into host app
   */
  isLoggedIntoHostApp?: boolean | null
  /**
   * Whether ClientSDK detects standalone MiniPay
   */
  isMinipayInstalled?: boolean | null
  /**
   * Whether ClientSDK detects available Google Play
   */
  isGooglePlayAvailable?: boolean | null
  /**
   * Canonical MiniPay wallet hash
   */
  minipayWalletHash?: string | null
  /**
   * Diagnostic info reported by ClientSDK
   */
  diagnosticInfo?: DiagnosticInfo | null
  /**
   * Whether user has completed self verification
   */
  isSelfVerifyCompleted?: boolean | null
}

export enum ClientType {
  MINI = 'mini',
  MINI_BETA = 'mini-beta',
  OFA = 'ofa',
  OFA_BETA = 'ofa-beta',
  NEWS = 'news'
}

/**
 * Payload to clone a campaign into a new Draft.
 */
export type CloneCampaignRequest = {
  /**
   * Internal name for the new cloned Draft.
   */
  name: string
}

export type CodeUploadResult = {
  inserted: number
  duplicates: number
  errors: Array<string>
}

export type ConfigResponse = {
  featureFlags: null
}

export type CounterMissionExtra = {
  cryptoTokenEarnLog: CryptoTokenEarnLogResponse
}

/**
 * Counter-report state embedded in a rejected counter-update error.
 */
export type CounterMissionRejectedReportResponse = {
  /**
   * The counter state when the reported update was rejected.
   */
  counter: CounterState | null
  /**
   * The posted count was rejected by the server.
   */
  outcome: 'rejected'
}

export type CounterMissionReportPayload = {
  count: number
  updatedAt: number
  /**
   * Optional field for debugging streaks functionality. It simulates a time shift (in milliseconds) on the server. This field is only supported for streak missions and doesn't work in a production environment.
   */
  timeOffset?: number | null
}

/**
 * Successful or asynchronously queued counter-report result.
 */
export type CounterMissionReportResponse = {
  /**
   * An updated counter object as defined in the mission object. Could be null in case of asynchronous processing.
   */
  counter: CounterState | null
  /**
   * 'accepted' if the posted count was accepted by the server or 'unknown' if the server queued it for asynchronous processing.
   */
  outcome: 'accepted' | 'unknown'
  /**
   * The crypto-token reward accrued by this accepted check-in, or null.
   */
  extra: CounterMissionExtra | null
}

export type CounterState = {
  /**
   * Current mission count.
   */
  count: number
  /**
   * The counter limit. Upon reaching this limit the mission is considered complete. `null` for unlimited.
   */
  limit: number | null
  /**
   * Step at which the client should report the missions progress to the server. For example, if "counterStep" is 5 the client would report counter value to the server upon reaching 5, 10, 15, etc. Default is 1.
   */
  step?: number
  /**
   * Unix time stamp in ms when the counter was updated last time or `null`. This value is used to merge the server pushed data on the client: if the client has a higher value of "counterUpdatedAt" than the server, the client keeps the local counters intact.
   */
  updatedAt: number | null
}

export type CounterUpdateFailedApiErrorBody = {
  detail: string
  errorCode: 'counter_update_failed_error'
  extra: CounterMissionRejectedReportResponse
}

export type CounterUpdateNothingToDoApiErrorBody = {
  detail: string
  errorCode: 'counter_update_nothing_to_do'
  extra: CounterMissionRejectedReportResponse
}

export type CounterUpdateStatusConflictApiErrorBody = {
  detail: string
  errorCode: 'counter_update_status_conflict'
  extra: CounterMissionRejectedReportResponse
}

export type CouponBanner = {
  iconUrl: string | null
  text: string
}

export type CouponCampaignFeatureDisabledApiErrorBody = {
  detail: string
  errorCode: 'coupon_campaign_feature_disabled'
}

export type CouponCampaignItemExtra = {
  title: Translatable
  banner: CouponBanner
  rows: Array<CouponDetailRow>
  buttonText: Translatable
}

export type CouponCampaignItemsResponse = {
  items: Array<CouponCampaignMarketItem>
}

export type CouponCampaignMarketItem = {
  id: number
  price: Amount
  state: MarketItemState
  offerHash: string
  extra: CouponCampaignItemExtra
  title: string
  iconUrl: string | null
}

export type CouponCampaignNotFoundApiErrorBody = {
  detail: string
  errorCode: 'coupon_campaign_not_found'
}

export type CouponCampaignPurchaseRequest = {
  id: number
  offerHash: string
}

export type CouponCampaignPurchaseResponse = {
  title: Translatable
  banner: CouponBanner
  code: string
  expiresAt: string | null
  details: CouponClaimDetails
  primaryAction: CouponPrimaryAction
  secondaryActionText: Translatable
}

export type CouponCampaignSoldOutApiErrorBody = {
  detail: string
  errorCode: 'coupon_campaign_sold_out'
}

export type CouponClaimDetails = {
  title: Translatable
  steps: Array<string>
}

export type CouponDetailRow = {
  label: Translatable
  value: string
}

export type CouponPrimaryAction = {
  text: Translatable
  url: string | null
}

/**
 * Payload to create a new Draft bonus campaign.
 */
export type CreateBonusCampaignRequest = {
  /**
   * Internal name shown to operators. It does not identify the campaign and does not need to be unique.
   */
  name: string
  /**
   * Targeting whose current membership determines eligible users.
   */
  targetingId: number
  /**
   * Campaign start as an absolute UTC instant.
   */
  startsAt: string
  /**
   * Exclusive campaign end as an absolute UTC instant.
   */
  endsAt: string
  /**
   * IANA timezone used to calculate recurring occurrence boundaries.
   */
  timezone:
    | 'Africa/Abidjan'
    | 'Africa/Accra'
    | 'Africa/Addis_Ababa'
    | 'Africa/Algiers'
    | 'Africa/Asmara'
    | 'Africa/Asmera'
    | 'Africa/Bamako'
    | 'Africa/Bangui'
    | 'Africa/Banjul'
    | 'Africa/Bissau'
    | 'Africa/Blantyre'
    | 'Africa/Brazzaville'
    | 'Africa/Bujumbura'
    | 'Africa/Cairo'
    | 'Africa/Casablanca'
    | 'Africa/Ceuta'
    | 'Africa/Conakry'
    | 'Africa/Dakar'
    | 'Africa/Dar_es_Salaam'
    | 'Africa/Djibouti'
    | 'Africa/Douala'
    | 'Africa/El_Aaiun'
    | 'Africa/Freetown'
    | 'Africa/Gaborone'
    | 'Africa/Harare'
    | 'Africa/Johannesburg'
    | 'Africa/Juba'
    | 'Africa/Kampala'
    | 'Africa/Khartoum'
    | 'Africa/Kigali'
    | 'Africa/Kinshasa'
    | 'Africa/Lagos'
    | 'Africa/Libreville'
    | 'Africa/Lome'
    | 'Africa/Luanda'
    | 'Africa/Lubumbashi'
    | 'Africa/Lusaka'
    | 'Africa/Malabo'
    | 'Africa/Maputo'
    | 'Africa/Maseru'
    | 'Africa/Mbabane'
    | 'Africa/Mogadishu'
    | 'Africa/Monrovia'
    | 'Africa/Nairobi'
    | 'Africa/Ndjamena'
    | 'Africa/Niamey'
    | 'Africa/Nouakchott'
    | 'Africa/Ouagadougou'
    | 'Africa/Porto-Novo'
    | 'Africa/Sao_Tome'
    | 'Africa/Timbuktu'
    | 'Africa/Tripoli'
    | 'Africa/Tunis'
    | 'Africa/Windhoek'
    | 'America/Adak'
    | 'America/Anchorage'
    | 'America/Anguilla'
    | 'America/Antigua'
    | 'America/Araguaina'
    | 'America/Argentina/Buenos_Aires'
    | 'America/Argentina/Catamarca'
    | 'America/Argentina/ComodRivadavia'
    | 'America/Argentina/Cordoba'
    | 'America/Argentina/Jujuy'
    | 'America/Argentina/La_Rioja'
    | 'America/Argentina/Mendoza'
    | 'America/Argentina/Rio_Gallegos'
    | 'America/Argentina/Salta'
    | 'America/Argentina/San_Juan'
    | 'America/Argentina/San_Luis'
    | 'America/Argentina/Tucuman'
    | 'America/Argentina/Ushuaia'
    | 'America/Aruba'
    | 'America/Asuncion'
    | 'America/Atikokan'
    | 'America/Atka'
    | 'America/Bahia'
    | 'America/Bahia_Banderas'
    | 'America/Barbados'
    | 'America/Belem'
    | 'America/Belize'
    | 'America/Blanc-Sablon'
    | 'America/Boa_Vista'
    | 'America/Bogota'
    | 'America/Boise'
    | 'America/Buenos_Aires'
    | 'America/Cambridge_Bay'
    | 'America/Campo_Grande'
    | 'America/Cancun'
    | 'America/Caracas'
    | 'America/Catamarca'
    | 'America/Cayenne'
    | 'America/Cayman'
    | 'America/Chicago'
    | 'America/Chihuahua'
    | 'America/Ciudad_Juarez'
    | 'America/Coral_Harbour'
    | 'America/Cordoba'
    | 'America/Costa_Rica'
    | 'America/Coyhaique'
    | 'America/Creston'
    | 'America/Cuiaba'
    | 'America/Curacao'
    | 'America/Danmarkshavn'
    | 'America/Dawson'
    | 'America/Dawson_Creek'
    | 'America/Denver'
    | 'America/Detroit'
    | 'America/Dominica'
    | 'America/Edmonton'
    | 'America/Eirunepe'
    | 'America/El_Salvador'
    | 'America/Ensenada'
    | 'America/Fort_Nelson'
    | 'America/Fort_Wayne'
    | 'America/Fortaleza'
    | 'America/Glace_Bay'
    | 'America/Godthab'
    | 'America/Goose_Bay'
    | 'America/Grand_Turk'
    | 'America/Grenada'
    | 'America/Guadeloupe'
    | 'America/Guatemala'
    | 'America/Guayaquil'
    | 'America/Guyana'
    | 'America/Halifax'
    | 'America/Havana'
    | 'America/Hermosillo'
    | 'America/Indiana/Indianapolis'
    | 'America/Indiana/Knox'
    | 'America/Indiana/Marengo'
    | 'America/Indiana/Petersburg'
    | 'America/Indiana/Tell_City'
    | 'America/Indiana/Vevay'
    | 'America/Indiana/Vincennes'
    | 'America/Indiana/Winamac'
    | 'America/Indianapolis'
    | 'America/Inuvik'
    | 'America/Iqaluit'
    | 'America/Jamaica'
    | 'America/Jujuy'
    | 'America/Juneau'
    | 'America/Kentucky/Louisville'
    | 'America/Kentucky/Monticello'
    | 'America/Knox_IN'
    | 'America/Kralendijk'
    | 'America/La_Paz'
    | 'America/Lima'
    | 'America/Los_Angeles'
    | 'America/Louisville'
    | 'America/Lower_Princes'
    | 'America/Maceio'
    | 'America/Managua'
    | 'America/Manaus'
    | 'America/Marigot'
    | 'America/Martinique'
    | 'America/Matamoros'
    | 'America/Mazatlan'
    | 'America/Mendoza'
    | 'America/Menominee'
    | 'America/Merida'
    | 'America/Metlakatla'
    | 'America/Mexico_City'
    | 'America/Miquelon'
    | 'America/Moncton'
    | 'America/Monterrey'
    | 'America/Montevideo'
    | 'America/Montreal'
    | 'America/Montserrat'
    | 'America/Nassau'
    | 'America/New_York'
    | 'America/Nipigon'
    | 'America/Nome'
    | 'America/Noronha'
    | 'America/North_Dakota/Beulah'
    | 'America/North_Dakota/Center'
    | 'America/North_Dakota/New_Salem'
    | 'America/Nuuk'
    | 'America/Ojinaga'
    | 'America/Panama'
    | 'America/Pangnirtung'
    | 'America/Paramaribo'
    | 'America/Phoenix'
    | 'America/Port-au-Prince'
    | 'America/Port_of_Spain'
    | 'America/Porto_Acre'
    | 'America/Porto_Velho'
    | 'America/Puerto_Rico'
    | 'America/Punta_Arenas'
    | 'America/Rainy_River'
    | 'America/Rankin_Inlet'
    | 'America/Recife'
    | 'America/Regina'
    | 'America/Resolute'
    | 'America/Rio_Branco'
    | 'America/Rosario'
    | 'America/Santa_Isabel'
    | 'America/Santarem'
    | 'America/Santiago'
    | 'America/Santo_Domingo'
    | 'America/Sao_Paulo'
    | 'America/Scoresbysund'
    | 'America/Shiprock'
    | 'America/Sitka'
    | 'America/St_Barthelemy'
    | 'America/St_Johns'
    | 'America/St_Kitts'
    | 'America/St_Lucia'
    | 'America/St_Thomas'
    | 'America/St_Vincent'
    | 'America/Swift_Current'
    | 'America/Tegucigalpa'
    | 'America/Thule'
    | 'America/Thunder_Bay'
    | 'America/Tijuana'
    | 'America/Toronto'
    | 'America/Tortola'
    | 'America/Vancouver'
    | 'America/Virgin'
    | 'America/Whitehorse'
    | 'America/Winnipeg'
    | 'America/Yakutat'
    | 'America/Yellowknife'
    | 'Antarctica/Casey'
    | 'Antarctica/Davis'
    | 'Antarctica/DumontDUrville'
    | 'Antarctica/Macquarie'
    | 'Antarctica/Mawson'
    | 'Antarctica/McMurdo'
    | 'Antarctica/Palmer'
    | 'Antarctica/Rothera'
    | 'Antarctica/South_Pole'
    | 'Antarctica/Syowa'
    | 'Antarctica/Troll'
    | 'Antarctica/Vostok'
    | 'Arctic/Longyearbyen'
    | 'Asia/Aden'
    | 'Asia/Almaty'
    | 'Asia/Amman'
    | 'Asia/Anadyr'
    | 'Asia/Aqtau'
    | 'Asia/Aqtobe'
    | 'Asia/Ashgabat'
    | 'Asia/Ashkhabad'
    | 'Asia/Atyrau'
    | 'Asia/Baghdad'
    | 'Asia/Bahrain'
    | 'Asia/Baku'
    | 'Asia/Bangkok'
    | 'Asia/Barnaul'
    | 'Asia/Beirut'
    | 'Asia/Bishkek'
    | 'Asia/Brunei'
    | 'Asia/Calcutta'
    | 'Asia/Chita'
    | 'Asia/Choibalsan'
    | 'Asia/Chongqing'
    | 'Asia/Chungking'
    | 'Asia/Colombo'
    | 'Asia/Dacca'
    | 'Asia/Damascus'
    | 'Asia/Dhaka'
    | 'Asia/Dili'
    | 'Asia/Dubai'
    | 'Asia/Dushanbe'
    | 'Asia/Famagusta'
    | 'Asia/Gaza'
    | 'Asia/Harbin'
    | 'Asia/Hebron'
    | 'Asia/Ho_Chi_Minh'
    | 'Asia/Hong_Kong'
    | 'Asia/Hovd'
    | 'Asia/Irkutsk'
    | 'Asia/Istanbul'
    | 'Asia/Jakarta'
    | 'Asia/Jayapura'
    | 'Asia/Jerusalem'
    | 'Asia/Kabul'
    | 'Asia/Kamchatka'
    | 'Asia/Karachi'
    | 'Asia/Kashgar'
    | 'Asia/Kathmandu'
    | 'Asia/Katmandu'
    | 'Asia/Khandyga'
    | 'Asia/Kolkata'
    | 'Asia/Krasnoyarsk'
    | 'Asia/Kuala_Lumpur'
    | 'Asia/Kuching'
    | 'Asia/Kuwait'
    | 'Asia/Macao'
    | 'Asia/Macau'
    | 'Asia/Magadan'
    | 'Asia/Makassar'
    | 'Asia/Manila'
    | 'Asia/Muscat'
    | 'Asia/Nicosia'
    | 'Asia/Novokuznetsk'
    | 'Asia/Novosibirsk'
    | 'Asia/Omsk'
    | 'Asia/Oral'
    | 'Asia/Phnom_Penh'
    | 'Asia/Pontianak'
    | 'Asia/Pyongyang'
    | 'Asia/Qatar'
    | 'Asia/Qostanay'
    | 'Asia/Qyzylorda'
    | 'Asia/Rangoon'
    | 'Asia/Riyadh'
    | 'Asia/Saigon'
    | 'Asia/Sakhalin'
    | 'Asia/Samarkand'
    | 'Asia/Seoul'
    | 'Asia/Shanghai'
    | 'Asia/Singapore'
    | 'Asia/Srednekolymsk'
    | 'Asia/Taipei'
    | 'Asia/Tashkent'
    | 'Asia/Tbilisi'
    | 'Asia/Tehran'
    | 'Asia/Tel_Aviv'
    | 'Asia/Thimbu'
    | 'Asia/Thimphu'
    | 'Asia/Tokyo'
    | 'Asia/Tomsk'
    | 'Asia/Ujung_Pandang'
    | 'Asia/Ulaanbaatar'
    | 'Asia/Ulan_Bator'
    | 'Asia/Urumqi'
    | 'Asia/Ust-Nera'
    | 'Asia/Vientiane'
    | 'Asia/Vladivostok'
    | 'Asia/Yakutsk'
    | 'Asia/Yangon'
    | 'Asia/Yekaterinburg'
    | 'Asia/Yerevan'
    | 'Atlantic/Azores'
    | 'Atlantic/Bermuda'
    | 'Atlantic/Canary'
    | 'Atlantic/Cape_Verde'
    | 'Atlantic/Faeroe'
    | 'Atlantic/Faroe'
    | 'Atlantic/Jan_Mayen'
    | 'Atlantic/Madeira'
    | 'Atlantic/Reykjavik'
    | 'Atlantic/South_Georgia'
    | 'Atlantic/St_Helena'
    | 'Atlantic/Stanley'
    | 'Australia/ACT'
    | 'Australia/Adelaide'
    | 'Australia/Brisbane'
    | 'Australia/Broken_Hill'
    | 'Australia/Canberra'
    | 'Australia/Currie'
    | 'Australia/Darwin'
    | 'Australia/Eucla'
    | 'Australia/Hobart'
    | 'Australia/LHI'
    | 'Australia/Lindeman'
    | 'Australia/Lord_Howe'
    | 'Australia/Melbourne'
    | 'Australia/NSW'
    | 'Australia/North'
    | 'Australia/Perth'
    | 'Australia/Queensland'
    | 'Australia/South'
    | 'Australia/Sydney'
    | 'Australia/Tasmania'
    | 'Australia/Victoria'
    | 'Australia/West'
    | 'Australia/Yancowinna'
    | 'Brazil/Acre'
    | 'Brazil/DeNoronha'
    | 'Brazil/East'
    | 'Brazil/West'
    | 'CET'
    | 'CST6CDT'
    | 'Canada/Atlantic'
    | 'Canada/Central'
    | 'Canada/Eastern'
    | 'Canada/Mountain'
    | 'Canada/Newfoundland'
    | 'Canada/Pacific'
    | 'Canada/Saskatchewan'
    | 'Canada/Yukon'
    | 'Chile/Continental'
    | 'Chile/EasterIsland'
    | 'Cuba'
    | 'EET'
    | 'EST'
    | 'EST5EDT'
    | 'Egypt'
    | 'Eire'
    | 'Etc/GMT'
    | 'Etc/GMT+0'
    | 'Etc/GMT+1'
    | 'Etc/GMT+10'
    | 'Etc/GMT+11'
    | 'Etc/GMT+12'
    | 'Etc/GMT+2'
    | 'Etc/GMT+3'
    | 'Etc/GMT+4'
    | 'Etc/GMT+5'
    | 'Etc/GMT+6'
    | 'Etc/GMT+7'
    | 'Etc/GMT+8'
    | 'Etc/GMT+9'
    | 'Etc/GMT-0'
    | 'Etc/GMT-1'
    | 'Etc/GMT-10'
    | 'Etc/GMT-11'
    | 'Etc/GMT-12'
    | 'Etc/GMT-13'
    | 'Etc/GMT-14'
    | 'Etc/GMT-2'
    | 'Etc/GMT-3'
    | 'Etc/GMT-4'
    | 'Etc/GMT-5'
    | 'Etc/GMT-6'
    | 'Etc/GMT-7'
    | 'Etc/GMT-8'
    | 'Etc/GMT-9'
    | 'Etc/GMT0'
    | 'Etc/Greenwich'
    | 'Etc/UCT'
    | 'Etc/UTC'
    | 'Etc/Universal'
    | 'Etc/Zulu'
    | 'Europe/Amsterdam'
    | 'Europe/Andorra'
    | 'Europe/Astrakhan'
    | 'Europe/Athens'
    | 'Europe/Belfast'
    | 'Europe/Belgrade'
    | 'Europe/Berlin'
    | 'Europe/Bratislava'
    | 'Europe/Brussels'
    | 'Europe/Bucharest'
    | 'Europe/Budapest'
    | 'Europe/Busingen'
    | 'Europe/Chisinau'
    | 'Europe/Copenhagen'
    | 'Europe/Dublin'
    | 'Europe/Gibraltar'
    | 'Europe/Guernsey'
    | 'Europe/Helsinki'
    | 'Europe/Isle_of_Man'
    | 'Europe/Istanbul'
    | 'Europe/Jersey'
    | 'Europe/Kaliningrad'
    | 'Europe/Kiev'
    | 'Europe/Kirov'
    | 'Europe/Kyiv'
    | 'Europe/Lisbon'
    | 'Europe/Ljubljana'
    | 'Europe/London'
    | 'Europe/Luxembourg'
    | 'Europe/Madrid'
    | 'Europe/Malta'
    | 'Europe/Mariehamn'
    | 'Europe/Minsk'
    | 'Europe/Monaco'
    | 'Europe/Moscow'
    | 'Europe/Nicosia'
    | 'Europe/Oslo'
    | 'Europe/Paris'
    | 'Europe/Podgorica'
    | 'Europe/Prague'
    | 'Europe/Riga'
    | 'Europe/Rome'
    | 'Europe/Samara'
    | 'Europe/San_Marino'
    | 'Europe/Sarajevo'
    | 'Europe/Saratov'
    | 'Europe/Simferopol'
    | 'Europe/Skopje'
    | 'Europe/Sofia'
    | 'Europe/Stockholm'
    | 'Europe/Tallinn'
    | 'Europe/Tirane'
    | 'Europe/Tiraspol'
    | 'Europe/Ulyanovsk'
    | 'Europe/Uzhgorod'
    | 'Europe/Vaduz'
    | 'Europe/Vatican'
    | 'Europe/Vienna'
    | 'Europe/Vilnius'
    | 'Europe/Volgograd'
    | 'Europe/Warsaw'
    | 'Europe/Zagreb'
    | 'Europe/Zaporozhye'
    | 'Europe/Zurich'
    | 'Factory'
    | 'GB'
    | 'GB-Eire'
    | 'GMT'
    | 'GMT+0'
    | 'GMT-0'
    | 'GMT0'
    | 'Greenwich'
    | 'HST'
    | 'Hongkong'
    | 'Iceland'
    | 'Indian/Antananarivo'
    | 'Indian/Chagos'
    | 'Indian/Christmas'
    | 'Indian/Cocos'
    | 'Indian/Comoro'
    | 'Indian/Kerguelen'
    | 'Indian/Mahe'
    | 'Indian/Maldives'
    | 'Indian/Mauritius'
    | 'Indian/Mayotte'
    | 'Indian/Reunion'
    | 'Iran'
    | 'Israel'
    | 'Jamaica'
    | 'Japan'
    | 'Kwajalein'
    | 'Libya'
    | 'MET'
    | 'MST'
    | 'MST7MDT'
    | 'Mexico/BajaNorte'
    | 'Mexico/BajaSur'
    | 'Mexico/General'
    | 'NZ'
    | 'NZ-CHAT'
    | 'Navajo'
    | 'PRC'
    | 'PST8PDT'
    | 'Pacific/Apia'
    | 'Pacific/Auckland'
    | 'Pacific/Bougainville'
    | 'Pacific/Chatham'
    | 'Pacific/Chuuk'
    | 'Pacific/Easter'
    | 'Pacific/Efate'
    | 'Pacific/Enderbury'
    | 'Pacific/Fakaofo'
    | 'Pacific/Fiji'
    | 'Pacific/Funafuti'
    | 'Pacific/Galapagos'
    | 'Pacific/Gambier'
    | 'Pacific/Guadalcanal'
    | 'Pacific/Guam'
    | 'Pacific/Honolulu'
    | 'Pacific/Johnston'
    | 'Pacific/Kanton'
    | 'Pacific/Kiritimati'
    | 'Pacific/Kosrae'
    | 'Pacific/Kwajalein'
    | 'Pacific/Majuro'
    | 'Pacific/Marquesas'
    | 'Pacific/Midway'
    | 'Pacific/Nauru'
    | 'Pacific/Niue'
    | 'Pacific/Norfolk'
    | 'Pacific/Noumea'
    | 'Pacific/Pago_Pago'
    | 'Pacific/Palau'
    | 'Pacific/Pitcairn'
    | 'Pacific/Pohnpei'
    | 'Pacific/Ponape'
    | 'Pacific/Port_Moresby'
    | 'Pacific/Rarotonga'
    | 'Pacific/Saipan'
    | 'Pacific/Samoa'
    | 'Pacific/Tahiti'
    | 'Pacific/Tarawa'
    | 'Pacific/Tongatapu'
    | 'Pacific/Truk'
    | 'Pacific/Wake'
    | 'Pacific/Wallis'
    | 'Pacific/Yap'
    | 'Poland'
    | 'Portugal'
    | 'ROC'
    | 'ROK'
    | 'Singapore'
    | 'Turkey'
    | 'UCT'
    | 'US/Alaska'
    | 'US/Aleutian'
    | 'US/Arizona'
    | 'US/Central'
    | 'US/East-Indiana'
    | 'US/Eastern'
    | 'US/Hawaii'
    | 'US/Indiana-Starke'
    | 'US/Michigan'
    | 'US/Mountain'
    | 'US/Pacific'
    | 'US/Samoa'
    | 'UTC'
    | 'Universal'
    | 'W-SU'
    | 'WET'
    | 'Zulu'
  /**
   * Frequency used to derive claimable occurrences from `starts_at`.
   */
  frequency: BonusCampaignFrequency
  /**
   * Token in which the reward is denominated.
   */
  tokenType: CryptoTokenType
  /**
   * Reward committed for each successful earning, in the token's natural unit (e.g., `"0.5"` USDT).
   */
  rewardAmount: number | string
  /**
   * Maximum reward amount committed in each occurrence, in the token's natural unit (e.g., `"50"` USDT).
   */
  distributionLimit: number | string
}

export type CreateCampaignRequest = {
  partnerId: string
  clientTypes: Array<ClientType>
  countryCodes: Array<string>
  title: string
  description: string
  howToUse?: string | null
  logoUrl?: string | null
  claimSteps?: Array<string>
  redirectUrl?: string | null
  redirectButtonLabel?: string | null
  pointsCost: number
  maxClaimsPerPeriod?: number
  claimPeriod?: ClaimPeriod
  validFrom?: string | null
  validTo?: string | null
  supplyMethod: SupplyMethod
  apiEndpoint?: string | null
}

/**
 * Generic request schema for creating any market offer.
 */
export type CreateOfferRequest = {
  startDate?: string | null
  endDate?: string | null
  price: number
  countryCodes?: Array<string>
  clientTypes: Array<ClientType>
  /**
   * Max total purchases across all users per rolling 7-day window. None = no limit.
   */
  purchaseLimitWeekly?: number | null
  /**
   * Max total purchases across all users per rolling 30-day window. None = no limit.
   */
  purchaseLimitMonthly?: number | null
  /**
   * Max total all-time purchases across all users. None = no limit.
   */
  purchaseLimitTotal?: number | null
  /**
   * Max purchases per app_instance_id per rolling 7-day window. None = no limit.
   */
  purchaseLimitAppInstanceWeekly?: number | null
  targetingId?: number | null
  /**
   * ID of the item (icon_id or wallpaper_id)
   */
  itemId: number
}

export type CreateRewardDistributionRequest = {
  amount: number | string
  /**
   * Defaults to app instance's wallet address
   */
  walletAddress: string | null
  tokenType: CryptoTokenType
}

export type CreateTargetingRequest = {
  slugName: string
  priority?: number | null
}

/**
 * Request schema for creating a token offer.
 */
export type CreateTokenOfferRequest = {
  startDate?: string | null
  endDate?: string | null
  price: number
  countryCodes?: Array<string>
  clientTypes: Array<ClientType>
  /**
   * Max total purchases across all users per rolling 7-day window. None = no limit.
   */
  purchaseLimitWeekly?: number | null
  /**
   * Max total purchases across all users per rolling 30-day window. None = no limit.
   */
  purchaseLimitMonthly?: number | null
  /**
   * Max total all-time purchases across all users. None = no limit.
   */
  purchaseLimitTotal?: number | null
  /**
   * Max purchases per app_instance_id per rolling 7-day window. None = no limit.
   */
  purchaseLimitAppInstanceWeekly?: number | null
  targetingId?: number | null
  tokenType: MarketTokenType
  tokenAmount: number | string
}

export type CreateTrafficCampaignRequest = {
  slug: string
  targetUrl: string
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom: string
  validTo: string
  rewardAmount: number
  periodicity: TrafficPeriodicity
  featureFlag?: FeatureFlag | null
  minimalVersion?: string | null
  maximalVersion?: string | null
}

export type CryptoActivityMetadata = {
  bonusStatus?: 'AVAILABLE' | 'COMPLETED' | null
  bonusCampaignId?: number | null
  expiresAt?: string | null
  /**
   * The raw CryptoTokenEarnLog.id accepted as the earnEntryId request field by POST /api/v1/user/crypto/claim. Clients pass this value unchanged to the claim endpoint rather than parsing the activity entry's source-prefixed id.
   */
  earnEntryId: number
  /**
   * The latest claim status, or null if no claim attempt exists. A null status means no claim has been started; it does not imply the entitlement is currently claimable (e.g., an expired bonus earning with no claim log has `claimStatus: null` but cannot be claimed). Returned as-is: LIVE, CLAIMED, or EXPIRED. A LIVE status means a claim is in progress and the user can resume it. A CLAIMED status means the reward was successfully claimed and cannot be retried. An EXPIRED status means the previous claim expired and the reward can be claimed again.
   */
  claimStatus?: CryptoTokenClaimStatus | null
}

export type CryptoOfferMetadata = {
  bonusCampaignId: number
  expiresAt: string
}

export type CryptoPhoneValidationErrorBody = {
  detail: string
  errorCode: 'crypto_phone_validation_error'
  extra: CryptoPhoneValidationErrorPayload
}

export type CryptoPhoneValidationErrorPayload = {
  status_code: number
  response_body: string
  phone_number: string
}

export type CryptoTokenClaimAlreadyClaimedErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_already_claimed'
}

export type CryptoTokenClaimAmountTooLargeErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_amount_too_large'
}

export type CryptoTokenClaimEarnEntryExpiredErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_earn_entry_expired'
}

export type CryptoTokenClaimGasFeePendingErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_gas_fee_pending'
  extra: CryptoTokenClaimGasFeePendingExtra
}

export type CryptoTokenClaimGasFeePendingExtra = {
  retryAfterSeconds: number
}

export type CryptoTokenClaimGasFeeTooHighErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_gas_fee_too_high'
}

export type CryptoTokenClaimInProgressErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_in_progress'
}

export type CryptoTokenClaimNotOwnedErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_not_owned'
}

export type CryptoTokenClaimRequest = {
  earnEntryId: number
}

export type CryptoTokenClaimResponse = {
  contractAddress: string
  /**
   * `0x`-prefixed ABI-encoded calldata for the contract's `claim` call. Its persisted signed amount does not change on a live-claim repeat.
   */
  data: string
  /**
   * The claimed token, which is also the currency the client pays gas in.
   */
  feeCurrency: RewardCurrency
  /**
   * Estimated net reward: gross reward minus the current per-token paid-fee estimate across settled claims, without a funding buffer, bounded below by zero. May change on a repeat of the same live claim without changing signed `data`; do not use it to construct the transaction.
   */
  amount: CurrencyAmount
  claimId: string
  gasFeeStatus: GasFeeStatus
}

export enum CryptoTokenClaimStatus {
  LIVE = 'LIVE',
  CLAIMED = 'CLAIMED',
  EXPIRED = 'EXPIRED'
}

export type CryptoTokenClaimUnavailableErrorBody = {
  detail: string
  errorCode: 'crypto_chain_claim_service_error'
}

export type CryptoTokenClaimUnsupportedCurrencyErrorBody = {
  detail: string
  errorCode: 'crypto_token_claim_unsupported_currency'
}

export type CryptoTokenEarnLogResponse = {
  id: number
  amount: string
  currency: CryptoTokenType
}

export type CryptoTokenNoWalletBoundApiErrorBody = {
  detail: string
  errorCode: 'crypto_token_no_wallet_bound'
}

export type CryptoTokenRewardConfig = {
  kind?: 'crypto-token'
  tokenType: CryptoTokenType
  /**
   * Amount in the token's natural unit (e.g. 1.5 USDT).
   */
  amount: string
}

export enum CryptoTokenType {
  USDT = 'usdt',
  USDC = 'usdc',
  USDM = 'usdm',
  CELO = 'celo'
}

export type CurrencyAmount = {
  /**
   * Amount in the smallest unit of the currency: the ERC20 token's smallest unit, or whole points for POINTS.
   */
  value: string
  currency: RewardCurrency
}

export type CurrencyAmountToken = {
  /**
   * Amount in the smallest unit of the currency: the ERC20 token's smallest unit, or whole points for POINTS.
   */
  value: string
  currency: CryptoTokenType
}

export type CursorPage_ActivityEntry_ = {
  items: Array<ActivityEntry>
  total: number
  /**
   * Cursor to refetch the current page
   */
  current_page?: string | null
  /**
   * Cursor to refetch the current page starting from the last item
   */
  current_page_backwards?: string | null
  /**
   * Cursor for the previous page
   */
  previous_page?: string | null
  /**
   * Cursor for the next page
   */
  next_page?: string | null
}

export type CursorPage_PointsHistoryRecord_ = {
  items: Array<PointsHistoryRecord>
  total: number
  /**
   * Cursor to refetch the current page
   */
  current_page?: string | null
  /**
   * Cursor to refetch the current page starting from the last item
   */
  current_page_backwards?: string | null
  /**
   * Cursor for the previous page
   */
  previous_page?: string | null
  /**
   * Cursor for the next page
   */
  next_page?: string | null
}

export type CustomFormConfig = {
  slug: CustomFormSlug
  title: Translatable
  description: Translatable
  confirmation: Translatable
  jsonSchema: {
    [key: string]: unknown
  }
  uiSchema: {
    [key: string]: unknown
  }
  maxSubmissions: number
}

export type CustomFormConfigsResponse = {
  items: Array<CustomFormConfig>
}

export type CustomFormMaxSubmissionsReachedApiErrorBody = {
  detail: string
  errorCode: 'custom_form_max_submissions_reached'
}

export enum CustomFormSlug {
  MINIPAY_JACKPOT_CLAIM = 'minipay-jackpot-claim'
}

export type CustomFormSlugsResponse = {
  slugs: Array<string>
}

export type CustomFormSubmissionEntry = {
  id: number
  customFormSlug: CustomFormSlug
  appInstanceId: number
  data: {
    [key: string]: unknown
  }
  createdAt: string
}

export type CustomFormSubmissionRequest = {
  data: {
    [key: string]: unknown
  }
}

export type CustomFormSubmissionSchema = {
  data: {
    [key: string]: unknown
  }
  submittedAt: string
}

export type CustomFormSubmissionsListResponse = {
  items: Array<CustomFormSubmissionEntry>
  total: number
}

export type CustomFormSubmissionsResponse = {
  submissions: Array<CustomFormSubmissionSchema>
}

export type CustomFormValidationApiErrorBody = {
  detail: string
  errorCode: 'custom_form_validation_error'
}

/**
 * Product IDs that are backed by a database offer table (admin-manageable).
 */
export enum DBBackedProductId {
  PREMIUM_WALLPAPER = 'premium-wallpaper',
  PREMIUM_ICON = 'premium-icon',
  USDT = 'usdt',
  TOKENS = 'tokens'
}

export type DataSavedEventReport = {
  type: 'dataSaved'
  payload: DataSavedReportData
}

export type DataSavedMissionStatus = {
  rewardAmount: number
  title: string
  description: string
}

export type DataSavedReportData = {
  /**
   * Total # of kb saved on given day.
   */
  savedKb: number
}

export type DiagnosticInfo = {
  isRooted?: boolean | null
  isEmulator?: boolean | null
  appDataDir?: string | null
  packageName?: string | null
  installerPackageName?: string | null
}

export type DistributionHealthResponse = {
  isRunning: boolean
  pendingCount: number
  failedCount: number
  oldestPendingAt: string | null
}

export type DuplicateTrafficCampaignRequest = {
  slug: string
}

export type Erc20TransferSchema = {
  /**
   * Token contract address that emitted the Transfer event (checksummed EIP-55).
   */
  contractAddress: string
  /**
   * Sender wallet address (checksummed EIP-55 format).
   */
  fromAddress: string
  /**
   * Recipient wallet address (checksummed EIP-55 format).
   */
  toAddress: string
  /**
   * Token amount transferred, in the token's smallest unit (Wei for 18-decimal tokens).
   */
  valueWei: number
}

export enum ErrorCode {
  UNKNOWN = 'unknown',
  NOT_FOUND = 'not_found',
  ADMIN_TOKEN_SIGNATURE_EXPIRED = 'admin_token_signature_expired',
  ADMIN_INVALIDATE_CACHE_USER_NOT_FOUND = 'admin_invalidate_cache_user_not_found',
  ADMIN_TOKEN_INVALID_ALGORITHM = 'admin_token_invalid_algorithm',
  ADMIN_TOKEN_INVALID_AUDIENCE = 'admin_token_invalid_audience',
  ADMIN_TOKEN_INVALID_SIGNATURE = 'admin_token_invalid_signature',
  ADMIN_TOKEN_INVALID_CLAIMS = 'admin_token_invalid_claims',
  ADMIN_TOKEN_INVALID = 'admin_token_invalid',
  ADMIN_TOKEN_INVALID_SCOPE = 'admin_token_invalid_scope',
  ADMIN_INSUFFICIENT_PERMISSIONS = 'admin_insufficient_permissions',
  ADMIN_POINTS_NEGATIVE_BALANCE = 'admin_points_negative_balance',
  ADMIN_APP_INSTANCE_NOT_FOUND = 'admin_app_instance_not_found',
  ADMIN_APP_INSTANCE_NOT_FOUND_BY_DEVICE_ID = 'admin_app_instance_not_found_by_device_id',
  ADMIN_PUSHER_MESSAGE_APP_INSTANCE_NO_HUID = 'admin_pusher_message_app_instance_no_huid',
  ADMIN_PUSHER_MESSAGE_UNSUPPORTED_CLIENT_TYPE = 'admin_pusher_message_unsupported_client_type',
  ADMIN_APP_INSTANCE_HAS_NO_WALLET = 'admin_app_instance_has_no_wallet',
  ADMIN_CREATE_REWARD_DISTRIBUTION_REGISTRY_LIMIT_REACHED = 'admin_create_reward_distribution_registry_limit_reached',
  ADMIN_NOTIFICATION_QUEUE_DISABLED = 'admin_notification_queue_disabled',
  ADMIN_CACHE_TRACKER_DISABLED = 'admin_cache_tracker_disabled',
  ADMIN_SPOTIFY_VOUCHER_DISABLED = 'admin_spotify_voucher_disabled',
  ADMIN_SPOTIFY_VOUCHER_STOCK_UNAVAILABLE = 'admin_spotify_voucher_stock_unavailable',
  ADMIN_MARKET_OFFER_NOT_FOUND = 'admin_market_offer_not_found',
  ADMIN_MARKET_OFFER_OVERLAPS = 'admin_market_offer_overlaps',
  ADMIN_MARKET_OFFER_ALREADY_PURCHASED = 'admin_market_offer_already_purchased',
  ADMIN_MARKET_TOKEN_OFFER_NOT_FOUND = 'admin_market_token_offer_not_found',
  ADMIN_MARKET_TOKEN_PRODUCT_TYPE_NOT_SUPPORTED = 'admin_market_token_product_type_not_supported',
  ADMIN_MARKET_TARGETING_NOT_FOUND = 'admin_market_targeting_not_found',
  ADMIN_TARGETING_PRIORITY_REQUIRED = 'admin_targeting_priority_required',
  ADMIN_BONUS_CAMPAIGN_NOT_FOUND = 'admin_bonus_campaign_not_found',
  ADMIN_BONUS_CAMPAIGN_LIFECYCLE_ERROR = 'admin_bonus_campaign_lifecycle_error',
  ADMIN_BONUS_CAMPAIGN_ACTIVATION_ERROR = 'admin_bonus_campaign_activation_error',
  ADMIN_BONUS_CAMPAIGN_CLONE_ERROR = 'admin_bonus_campaign_clone_error',
  ADMIN_BONUS_CAMPAIGN_DELETE_ERROR = 'admin_bonus_campaign_delete_error',
  ADMIN_BONUS_CAMPAIGN_UPDATE_ERROR = 'admin_bonus_campaign_update_error',
  ADMIN_BONUS_CAMPAIGN_CREATE_ERROR = 'admin_bonus_campaign_create_error',
  ADMIN_TARGETING_NOT_FOUND = 'admin_targeting_not_found',
  ADMIN_TARGETING_ALREADY_EXISTS = 'admin_targeting_already_exists',
  ADMIN_TARGETING_HAS_APP_INSTANCES = 'admin_targeting_has_app_instances',
  ADMIN_TARGETING_ASSIGNED_TO_OFFER = 'admin_targeting_assigned_to_offer',
  ADMIN_TARGETING_SYNC_RUN_NOT_FOUND = 'admin_targeting_sync_run_not_found',
  ADMIN_TARGETING_SYNC_RUN_NOT_EXPIRABLE = 'admin_targeting_sync_run_not_expirable',
  ADMIN_TARGETING_PRIORITY_COLLISION = 'admin_targeting_priority_collision',
  STREAKS_CHECKIN_ALREADY_MADE_FOR_TODAY = 'streaks_checkin_already_made_for_today',
  STREAKS_CHECKIN_MISSION_IS_NOT_ENABLED_FOR_USER = 'streaks_checkin_mission_is_not_enabled_for_user',
  STREAKS_CHECKIN_ATTEMPT_DURING_ACTIVE_COOL_OFF = 'streaks_checkin_attempt_during_active_cool_off',
  STREAKS_CHECKIN_DATE_BEFORE_MAX_RECORD = 'streaks_checkin_date_before_max_record',
  GIVEAWAY_REWARD_CHOICE_NOT_AVAILABLE = 'giveaway_reward_choice_not_available',
  GIVEAWAY_PARTNER_CONNECTION_ERROR = 'giveaway_partner_connection_error',
  GIVEAWAY_ALREADY_CLAIMED_BY_ANOTHER_USER_SAME_HAID = 'giveaway_already_claimed_by_another_user_same_haid',
  GIVEAWAY_PHONE_OR_EMAIL_ALREADY_USED = 'giveaway_phone_or_email_already_used',
  GIVEAWAY_ALREADY_CLAIMED_BY_APP_INSTANCE = 'giveaway_already_claimed_by_app_instance',
  DATASAVED_MISSION_NOT_CONFIGURED = 'datasaved_mission_not_configured',
  DATASAVED_MISSION_NOT_ENABLED_FOR_USER = 'datasaved_mission_not_enabled_for_user',
  USER_ACTIVITY_BAD_CURSOR_FORMAT = 'user_activity_bad_cursor_format',
  USER_POINTS_NOT_ENABLED = 'user_points_not_enabled',
  USER_MISSION_NOT_ENABLED = 'user_mission_not_enabled',
  USER_MISSION_HISTORY_NOT_ENABLED = 'user_mission_history_not_enabled',
  USER_MISSION_TYPE_DOES_NOT_SUPPORT_HISTORY = 'user_mission_type_does_not_support_history',
  POINTS_HISTORY_BAD_CURSOR_FORMAT = 'points_history_bad_cursor_format',
  ROULETTE_MISSION_NOT_ENABLED_FOR_USER = 'roulette_mission_not_enabled_for_user',
  ROULETTE_REWARD_DRAW_FAILED = 'roulette_reward_draw_failed',
  ROULETTE_HAID_NO_SPIN_AVAILABLE = 'roulette_haid_no_spin_available',
  ROULETTE_NOT_ENOUGH_POINTS_TO_BUY_SPIN = 'roulette_not_enough_points_to_buy_spin',
  ROULETTE_INVALID_SIGNATURE = 'roulette_invalid_signature',
  ROULETTE_GAS_FEE_SERVICE_ERROR = 'roulette_gas_fee_service_error',
  AUTH_ACCESS_TOKEN_EXPIRED = 'auth_access_token_expired',
  AUTH_ACCESS_TOKEN_INVALID_ISSUER = 'auth_access_token_invalid_issuer',
  AUTH_ACCESS_TOKEN_INVALID_AUDIENCE = 'auth_access_token_invalid_audience',
  AUTH_ACCESS_TOKEN_MISSING = 'auth_access_token_missing',
  AUTH_ACCESS_TOKEN_INVALID_TYPE = 'auth_access_token_invalid_type',
  AUTH_ACCESS_TOKEN_INVALID_ALGORITHM = 'auth_access_token_invalid_algorithm',
  AUTH_ACCESS_TOKEN_INVALID_SIGNATURE = 'auth_access_token_invalid_signature',
  AUTH_ACCESS_TOKEN_INVALID_FORMAT = 'auth_access_token_invalid_format',
  AUTH_REFRESH_TOKEN_EXPIRED = 'auth_refresh_token_expired',
  AUTH_REFRESH_TOKEN_ALREADY_USED = 'auth_refresh_token_already_used',
  AUTH_REFRESH_TOKEN_INVALID_AUDIENCE = 'auth_refresh_token_invalid_audience',
  AUTH_REFRESH_TOKEN_INVALID_ISSUER = 'auth_refresh_token_invalid_issuer',
  AUTH_REFRESH_TOKEN_INVALID_ALGORITHM = 'auth_refresh_token_invalid_algorithm',
  AUTH_REFRESH_TOKEN_INVALID_SIGNATURE = 'auth_refresh_token_invalid_signature',
  AUTH_REFRESH_TOKEN_INVALID_FORMAT = 'auth_refresh_token_invalid_format',
  AUTH_REFRESH_TOKEN_INVALID_TYPE = 'auth_refresh_token_invalid_type',
  MARKET_WALLPAPER_OFFER_NOT_FOUND = 'market_wallpaper_offer_not_found',
  MARKET_WALLPAPER_ALREADY_PURCHASED = 'market_wallpaper_already_purchased',
  MARKET_ICON_ALREADY_PURCHASED = 'market_icon_already_purchased',
  MARKET_ICON_OFFER_NOT_FOUND = 'market_icon_offer_not_found',
  MARKET_INVALID_OFFER_HASH = 'market_invalid_offer_hash',
  MARKET_INSUFFICIENT_POINTS = 'market_insufficient_points',
  MARKET_USDT_OFFER_NOT_FOUND = 'market_usdt_offer_not_found',
  MARKET_USDT_PHONE_NOT_REGISTERED = 'market_usdt_phone_not_registered',
  MARKET_USDT_FEATURE_DISABLED = 'market_usdt_feature_disabled',
  MARKET_USDT_ALREADY_PURCHASED = 'market_usdt_already_purchased',
  MARKET_USDT_MONTHLY_LIMIT_REACHED = 'market_usdt_monthly_limit_reached',
  MARKET_USDT_WALLET_DAILY_LIMIT_REACHED = 'market_usdt_wallet_daily_limit_reached',
  MARKET_USDT_WALLET_MINUTE_LIMIT_REACHED = 'market_usdt_wallet_minute_limit_reached',
  MARKET_TOKEN_OFFER_NOT_FOUND = 'market_token_offer_not_found',
  MARKET_TOKEN_MONTHLY_LIMIT_REACHED = 'market_token_monthly_limit_reached',
  MARKET_SPOTIFY_VOUCHER_ALREADY_PURCHASED = 'market_spotify_voucher_already_purchased',
  MARKET_SPOTIFY_VOUCHER_NOT_AVAILABLE = 'market_spotify_voucher_not_available',
  MARKET_SPOTIFY_VOUCHER_FEATURE_DISABLED = 'market_spotify_voucher_feature_disabled',
  MARKET_SPOTIFY_VOUCHER_PROMO_SERVICE_ERROR = 'market_spotify_voucher_promo_service_error',
  MARKET_SPOTIFY_VOUCHER_NOT_PURCHASED = 'market_spotify_voucher_not_purchased',
  MARKET_SPOTIFY_VOUCHER_NO_OPERA_USER_ID = 'market_spotify_voucher_no_opera_user_id',
  MARKET_PURCHASE_LIMIT_EXCEEDED = 'market_purchase_limit_exceeded',
  COUNTER_UPDATE_FAILED_ERROR = 'counter_update_failed_error',
  COUNTER_UPDATE_STATUS_CONFLICT = 'counter_update_status_conflict',
  COUNTER_UPDATE_NOTHING_TO_DO = 'counter_update_nothing_to_do',
  DEBUG_ANONYMOUS_USER_OPERATION_NOT_ALLOWED = 'debug_anonymous_user_operation_not_allowed',
  DEBUG_USER_EMAIL_NOT_WHITELISTED = 'debug_user_email_not_whitelisted',
  DEBUG_USER_NO_FCM_TOKENS = 'debug_user_no_fcm_tokens',
  DEBUG_ENDPOINT_NOT_FOUND = 'debug_endpoint_not_found',
  CRYPTO_API_KEY_INVALID = 'crypto_api_key_invalid',
  CRYPTO_PHONE_VALIDATION_ERROR = 'crypto_phone_validation_error',
  CRYPTO_UNEXPECTED_RESPONSE = 'crypto_unexpected_response',
  CRYPTO_CHAIN_CLAIM_SERVICE_ERROR = 'crypto_chain_claim_service_error',
  CRYPTO_TOKEN_CLAIM_ALREADY_CLAIMED = 'crypto_token_claim_already_claimed',
  CRYPTO_TOKEN_CLAIM_NOT_OWNED = 'crypto_token_claim_not_owned',
  CRYPTO_TOKEN_CLAIM_IN_PROGRESS = 'crypto_token_claim_in_progress',
  CRYPTO_TOKEN_CLAIM_UNSUPPORTED_CURRENCY = 'crypto_token_claim_unsupported_currency',
  CRYPTO_TOKEN_CLAIM_AMOUNT_TOO_LARGE = 'crypto_token_claim_amount_too_large',
  CRYPTO_TOKEN_CLAIM_GAS_FEE_PENDING = 'crypto_token_claim_gas_fee_pending',
  CRYPTO_TOKEN_CLAIM_GAS_FEE_TOO_HIGH = 'crypto_token_claim_gas_fee_too_high',
  CRYPTO_TOKEN_CLAIM_EARN_ENTRY_EXPIRED = 'crypto_token_claim_earn_entry_expired',
  ADMIN_CELO_INVALID_ADDRESS = 'admin_celo_invalid_address',
  ADMIN_CELO_TRANSACTION_NOT_FOUND = 'admin_celo_transaction_not_found',
  ADMIN_CELO_RPC_UNAVAILABLE = 'admin_celo_rpc_unavailable',
  ADMIN_TASK_EXECUTION_NOT_FOUND = 'admin_task_execution_not_found',
  ADMIN_TASK_EXECUTION_INVALID_TIME_RANGE = 'admin_task_execution_invalid_time_range',
  ADMIN_TOKEN_PAYOUT_RUN_NOT_FOUND = 'admin_token_payout_run_not_found',
  ADMIN_TOKEN_PAYOUT_RUN_CANCEL_INVALID_STATUS = 'admin_token_payout_run_cancel_invalid_status',
  ADMIN_CHAIN_CLAIM_SERVICE_ERROR = 'admin_chain_claim_service_error',
  ADMIN_LEADERBOARD_KEY_INVALID = 'admin_leaderboard_key_invalid',
  CUSTOM_FORM_NOT_FOUND = 'custom_form_not_found',
  CUSTOM_FORM_MAX_SUBMISSIONS_REACHED = 'custom_form_max_submissions_reached',
  CUSTOM_FORM_VALIDATION_ERROR = 'custom_form_validation_error',
  AUTH_WALLET_NONCE_INVALID = 'auth_wallet_nonce_invalid',
  AUTH_WALLET_SIGNATURE_INVALID = 'auth_wallet_signature_invalid',
  AUTH_WALLET_ALREADY_BOUND = 'auth_wallet_already_bound',
  AUTH_WALLET_BINDING_NOT_ENABLED = 'auth_wallet_binding_not_enabled',
  BONUS_CLAIM_UNAVAILABLE = 'bonus_claim_unavailable',
  CRYPTO_TOKEN_NO_WALLET_BOUND = 'crypto_token_no_wallet_bound',
  AUTH_COUNTRY_UNRESOLVED = 'auth_country_unresolved',
  MARKET_CELO_INTENT_FEATURE_DISABLED = 'market_celo_intent_feature_disabled',
  MARKET_CELO_INTENT_NO_WALLET_BOUND = 'market_celo_intent_no_wallet_bound',
  MARKET_CELO_INTENT_ACTIVE_INTENT_EXISTS = 'market_celo_intent_active_intent_exists',
  MARKET_CELO_PURCHASE_INTENT_ACCESS_DENIED = 'market_celo_purchase_intent_access_denied',
  TOKEN_CASHOUT_TX_NOT_FOUND = 'token_cashout_tx_not_found',
  TOKEN_CASHOUT_TX_HAS_NO_TO_ADDRESS = 'token_cashout_tx_has_no_to_address',
  TOKEN_CASHOUT_TX_AMOUNT_MISMATCH = 'token_cashout_tx_amount_mismatch',
  TOKEN_CASHOUT_TX_HAS_NO_RECEIPT = 'token_cashout_tx_has_no_receipt',
  TOKEN_CASHOUT_TX_ALREADY_RECORDED = 'token_cashout_tx_already_recorded',
  TOKEN_CASHOUT_CELO_CONFIG_MISSING = 'token_cashout_celo_config_missing',
  COUPON_CAMPAIGN_FEATURE_DISABLED = 'coupon_campaign_feature_disabled',
  COUPON_CAMPAIGN_NOT_FOUND = 'coupon_campaign_not_found',
  COUPON_CAMPAIGN_SOLD_OUT = 'coupon_campaign_sold_out',
  ADMIN_COUPON_CAMPAIGN_NOT_FOUND = 'admin_coupon_campaign_not_found',
  ADMIN_COUPON_CAMPAIGN_CODE_DUPLICATE = 'admin_coupon_campaign_code_duplicate',
  LEADERBOARD_NOT_ENABLED = 'leaderboard_not_enabled',
  TRAFFIC_CAMPAIGN_SLUG_NOT_FOUND = 'traffic_campaign_slug_not_found',
  MISSION_CAMPAIGN_MISSING = 'mission_campaign_missing',
  MISSION_NOT_FOUND = 'mission_not_found',
  ADMIN_TRAFFIC_CAMPAIGN_NOT_FOUND = 'admin_traffic_campaign_not_found',
  ADMIN_TRAFFIC_CAMPAIGN_SLUG_EXISTS = 'admin_traffic_campaign_slug_exists',
  ADMIN_TRAFFIC_CAMPAIGN_CONFLICT = 'admin_traffic_campaign_conflict',
  ADMIN_TRAFFIC_CAMPAIGN_OPERATION_NOT_ALLOWED = 'admin_traffic_campaign_operation_not_allowed',
  ADMIN_TRAFFIC_CAMPAIGN_INVALID_CONFIGURATION = 'admin_traffic_campaign_invalid_configuration'
}

export type ErrorDetail = {
  detail: string
}

export type EventSchema = {
  id: number
  appInstanceId: number | null
  eventType: EventType
  clientType: string
  payload: {
    [key: string]: unknown
  }
  createdAt: string
}

export enum EventType {
  USER_SET_CLIENT_DEFAULT = 'user_set_client_default',
  LOYALTY_BONUS_DAILY_GRANT = 'loyalty_bonus_daily_grant',
  LOYALTY_BONUS_INITIAL_BONUS_GRANT = 'loyalty_bonus_initial_bonus_grant',
  STREAKS_CHECK_IN = 'streaks_check_in',
  GIVEAWAY_FORM_SENT = 'giveaway_form_sent',
  GIVEAWAY_STATUS_CHECK_REQUESTED = 'giveaway_status_check_requested',
  CLIENT_REPORTED_DATA_SAVED = 'client_reported_data_saved',
  ROULETTE_SPIN = 'roulette_spin',
  EAYB_GRANT = 'eayb_grant',
  COUNTER_REPORTED = 'counter_reported',
  POLLS_BET = 'polls_bet',
  MINIPAY_STANDALONE_INSTALLATION_REPORTED = 'minipay_standalone_installation_reported',
  MINIPAY_STANDALONE_CLICK_REPORTED = 'minipay_standalone_click_reported',
  MINIPAY_STANDALONE_ACTIVATION_REPORTED = 'minipay_standalone_activation_reported',
  SELF_VERIFICATION_COMPLETED = 'self_verification_completed'
}

export type EventsResponse = {
  items?: Array<EventSchema>
}

export enum FeatureFlag {
  SET_WALLPAPER_OFA = 'set_wallpaper_ofa',
  SET_WALLPAPER_OFA_BR = 'set_wallpaper_ofa_br',
  MAKE_BROWSER_DEFAULT = 'make_browser_default',
  MISSIONS_GLOBAL_NEWS = 'missions_global_news',
  MISSION_GIVEAWAY = 'mission_giveaway',
  DEBUG_HTTP_LOGGING = 'debug_http_logging',
  DEBUG_CLIENTSDK_DEV_SETTINGS = 'debug_clientsdk_dev_settings',
  MISSIONS_GLOBAL_ROLLOUT = 'missions_global_rollout',
  MISSION_MINIPAY_STANDALONE = 'mission_minipay_standalone',
  MISSION_WATCH_ADS = 'mission_watch_ads',
  SPOTIFY_VOUCHER = 'spotify_voucher',
  MISSION_HISTORY = 'mission_history',
  BANNERS_AVAILABLE = 'banners_available',
  TOP_POINTS_EARNER = 'top_points_earner',
  CELO_FEATURES = 'celo_features',
  TRAFFIC_CAMPAIGNS_DB = 'traffic_campaigns_db',
  TRAFFIC_CAMPAIGN_TEST = 'traffic_campaign_test',
  IS_VIP_USER = 'is_vip_user',
  VIP_LEVEL_CHECK_ENABLED = 'vip_level_check_enabled',
  CELO_PAYOUT_NOTIFICATION = 'celo_payout_notification',
  LEADERBOARD_PAYOUT_ENABLED = 'leaderboard_payout_enabled',
  CELO_PURCHASE_INTENT = 'celo_purchase_intent',
  TOKEN_WALLET_DUSTING = 'token_wallet_dusting',
  MISSION_COMPLETED_NOTIFICATION = 'mission_completed_notification',
  CASHOUT_ENABLED = 'cashout_enabled',
  TARGETING_BITSTRING_CACHE = 'targeting_bitstring_cache',
  EAYB_NOTIFICATION = 'eayb_notification',
  TOKEN_OFFER_ENABLED = 'token_offer_enabled',
  MISSION_SELF_VERIFY = 'mission_self_verify',
  COUPON_CAMPAIGNS_AVAILABLE = 'coupon_campaigns_available',
  LEADERBOARD_ENABLED = 'leaderboard_enabled',
  LEADERBOARD_ENDPOINTS_ENABLED = 'leaderboard_endpoints_enabled',
  DEBUG_BYPASS_TOKEN_LIMITS = 'debug_bypass_token_limits',
  STREAKS_STABLECOIN_INTERNAL_TESTS = 'streaks_stablecoin_internal_tests',
  STREAKS_STABLECOIN = 'streaks_stablecoin',
  DEBUG_CASHOUT_NO_MIN_AMOUNT = 'debug_cashout_no_min_amount'
}

export type FeatureFlagSchema = {
  name: string
  enabled: boolean
}

export type FeatureFlagsResponse = {
  items: Array<FeatureFlagSchema>
  deviceId: string
  appInstanceId: number
  clientType: ClientType
}

export enum GasFeeStatus {
  COMPLETED = 'completed',
  PENDING = 'pending',
  NOT_QUALIFIED = 'not_qualified'
}

export type GenerateSignatureRequest = {
  claimId: number
  token: CryptoTokenType
  to: string
  amount: number
  expiresAt: string
}

/**
 * Extends the service response with the EVM calldata for MiniPaySign.claim().
 */
export type GenerateSignatureWithEncodedResponse = {
  signature: string
  signerAddress: string
  contractAddress: string
  tokenAddress: string
  encodedClaimData: string
}

export enum GiveawayEligibilityStatus {
  ELIGIBLE = 'ELIGIBLE',
  CLAIMED_BY_APP_INSTANCE = 'CLAIMED_BY_APP_INSTANCE',
  MISSION_DISABLED = 'MISSION_DISABLED',
  TECHNICAL_DIFFICULTIES = 'TECHNICAL_DIFFICULTIES'
}

export type GiveawayMissionStatus = {
  validTo: string | null
  completed: boolean
}

/**
 * Sync between db enum values and possible options in yaml config.
 */
export enum GiveawayRewardChoice {
  FREE_DATA = 'free_data',
  UBER = 'uber',
  SHOPEE_VOUCHER_RS25 = 'shopee_voucher_rs25',
  CLARO_VOUCHER_FREE_DATA = 'claro_voucher_free_data',
  SHOPEE_VOUCHER_RS5 = 'shopee_voucher_rs5'
}

export type GiveawayRewardChoiceMinuConfig = {
  /**
   * When should we send notification that rewards are about to run out?e.g. if we have 100 rewards and limit is 0.9 then the Slack notification will fire when we exceed 90 rewards claimed.The check is being run once an hour and it's going to continue notifying until campaign is disabled or we go above threshold.Set to 0 to disable.
   */
  rewardLimitNotificationThreshold?: number
  /**
   * Unique id of given reward choice
   */
  choiceId: GiveawayRewardChoice
  rewardRedeemFlow?: 'minu'
  experienceId: string
  rewardValue?: number
  /**
   * Max number of successful forms sent and therefore max number of rewards PER MONTH that we're going to share in given giveaway campaign
   */
  maxWinsPerMonth: number
}

export type GiveawayRewardChoiceVoucherConfig = {
  /**
   * When should we send notification that rewards are about to run out?e.g. if we have 100 rewards and limit is 0.9 then the Slack notification will fire when we exceed 90 rewards claimed.The check is being run once an hour and it's going to continue notifying until campaign is disabled or we go above threshold.Set to 0 to disable.
   */
  rewardLimitNotificationThreshold?: number
  /**
   * Unique id of given reward choice
   */
  choiceId: GiveawayRewardChoice
  rewardRedeemFlow?: 'voucher'
  /**
   * Unique identifier of given voucher in vouchers system.
   */
  voucherType: VoucherType
}

export type GiveawayStatus = {
  status: GiveawayEligibilityStatus
  rewardChoices: Array<GiveawayRewardChoice>
  rewardChoiceConfigs: Array<RewardChoiceConfig>
}

export type HTTPValidationError = {
  detail?: Array<ValidationError>
}

export type HealthcheckResponse = {
  status: Status
}

export type HiddenRewardsMissionStatus = {
  validTo: string | null
  foundRewards: number
  rewardsLimit: number
}

export type IconAlreadyPurchasedApiErrorBody = {
  detail: string
  errorCode: 'market_icon_already_purchased'
}

export type IconItem = {
  /**
   * ID of icon in icon repository
   */
  id: number
  price: Amount
  state: MarketItemState
  offerHash: string
  /**
   * Additional offer-specific data (e.g., usdt_amount for USDT offers)
   */
  extra?: CamelModel | null
}

export type IconOfferNotFoundApiErrorBody = {
  detail: string
  errorCode: 'market_icon_offer_not_found'
}

export type IconPurchaseRequest = {
  /**
   * ID of icon in icon repository
   */
  id: number
  offerHash: string
}

export type IconPurchaseResponse = {
  /**
   * ID of icon in icon repository
   */
  id: number
  state: MarketItemState
}

export type IconsResponse = {
  items: Array<IconItem>
}

/**
 * Translatable template string (may contain format placeholders)
 */
export type InlineTranslatable = string

export type InsufficientPointsApiErrorBody = {
  detail: string
  errorCode: 'market_insufficient_points'
}

export type InvalidOfferHashApiErrorBody = {
  detail: string
  errorCode: 'market_invalid_offer_hash'
}

export type InvalidateKeysRequestSchema = {
  keys: Array<string>
}

export type InvalidateKeysResponseSchema = {
  invalidated_count: number
}

export type LeaderboardBannerResponse = {
  /**
   * URL of the leaderboard banner icon.
   */
  iconUrl: string
  /**
   * Localized leaderboard banner text.
   */
  text: Translatable
}

export type LeaderboardConfigResponse = {
  /**
   * Banner describing the weekly leaderboard rewards.
   */
  banner: LeaderboardBannerResponse
  /**
   * Configured leaderboard tiers, ordered by ascending tier number.
   */
  tiers: Array<LeaderboardTierConfigResponse>
}

export type LeaderboardEligibilitySchema = {
  isAllowed: boolean
  requirements: Array<LeaderboardRequirementStateSchema>
}

export type LeaderboardMembershipSchema = {
  isMember: boolean
  lastStateChangeAt: string | null
}

export type LeaderboardNotEnabledApiErrorBody = {
  detail: string
  errorCode: 'leaderboard_not_enabled'
}

export type LeaderboardRankSchema = {
  rank: number | null
  score: number | null
  tier: number | null
}

/**
 * A single user-facing condition an app instance must meet to join the leaderboard.
 *
 * Which requirements apply depends on the client type (see app/leaderboard/README.md).
 */
export enum LeaderboardRequirement {
  MAKE_BROWSER_DEFAULT = 'make_browser_default',
  GOOGLE_SIGNIN = 'google_signin',
  SIGNIN = 'signin',
  CLIENT_VERSION = 'client_version',
  MINIPAY_ACTIVATED = 'minipay_activated'
}

export type LeaderboardRequirementResponse = {
  /**
   * Name of the requirement that the user must satisfy to join the leaderboard.
   */
  type: LeaderboardRequirement
  /**
   * Localized title displayed to the user for this requirement.
   */
  title: Translatable
  /**
   * Whether the user currently satisfies this requirement.
   */
  isSatisfied: boolean
  /**
   * An Action to perform to satisfy the requirement.
   */
  action: Action | null
}

export type LeaderboardRequirementStateSchema = {
  requirement: LeaderboardRequirement
  isSatisfied: boolean
}

export type LeaderboardStandingResponse = {
  /**
   * Estimated tier for the user's current weekly rank.
   */
  tier: number
  /**
   * User's rank in the current weekly leaderboard.
   */
  rank: number
  /**
   * Points earned by the user in the current leaderboard week.
   */
  score: number
}

export type LeaderboardStateResponse = {
  /**
   * Current weekly standing, or null when the user has no leaderboard entry.
   */
  standing: LeaderboardStandingResponse | null
  /**
   * Ordered leaderboard membership requirements and their current states.
   */
  requirements: Array<LeaderboardRequirementResponse>
}

export type LeaderboardTierConfigResponse = {
  /**
   * Tier number.
   */
  tier: number
  /**
   * Starting (inclusive) rank of the tier.
   */
  startRank: number
  /**
   * Ending (inclusive) rank of the tier.
   */
  endRank: number
  /**
   * Per-user reward for this tier, expressed in the token's smallest unit.
   */
  reward: CurrencyAmount
}

export type MarketInfoResponse = {
  usdt: UsdtMarketInfoSchema
  spotify: SpotifyMarketInfoSchema
}

/**
 * Reference to a market item associated with a points log entry.
 *
 * Allows the client to link a points history record back to the market
 * item that was purchased (e.g., to fetch redemption details).
 */
export type MarketItemReference = {
  productId: ProductId
  itemId: number
}

export enum MarketItemState {
  PURCHASED = 'PURCHASED',
  AVAILABLE_FOR_PURCHASE = 'AVAILABLE_FOR_PURCHASE'
}

export enum MarketTokenType {
  USDT = 'usdt',
  USDC = 'usdc'
}

export type MiniPayApp = {
  name: string
  link: string
}

export enum MinuRewardStatusError {
  PHONE_NUMBER_ALREADY_USED = 'PHONE_NUMBER_ALREADY_USED',
  INVALID_PHONE_NUMBER = 'INVALID_PHONE_NUMBER',
  FORM_DATA_VALIDATION_FAILED = 'FORM_DATA_VALIDATION_FAILED',
  NON_PARTICIPATING_OPERATOR = 'NON_PARTICIPATING_OPERATOR',
  UNKNOWN = 'UNKNOWN'
}

export type MissionBoostStatus = {
  /**
   * Boost multiplier.
   */
  value: number
  /**
   * True if user already satisfies the requirements for this boost.
   */
  active: boolean
}

export type MissionCompletionSchema = {
  appInstanceId: number
  missionCount: number
  countryCode: string
  clientType: ClientType
}

export type MissionConfigDataSaved = {
  configs: Array<MissionTargetedConfigDataSaved>
  missionType: 'data_saved'
}

export type MissionConfigEAYB = {
  configs: Array<MissionTargetedConfigEAYB>
  missionType: 'eayb'
}

export type MissionConfigGiveaway = {
  configs: Array<MissionTargetedConfigGiveaway>
  missionType: 'giveaway'
  rewardChoices: Array<GiveawayRewardChoiceMinuConfig | GiveawayRewardChoiceVoucherConfig>
}

export type MissionConfigGoogleSignin = {
  actions?: Action
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigGoogleSignin>
  missionType: 'google_signin'
}

export type MissionConfigMakeBrowserDefault = {
  actions?: {
    [key: string]: Action
  }
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigMakeBrowserDefault>
  missionType: 'make_browser_default'
}

/**
 * General configuration for MiniPay standalone mission.
 * Defines UI elements, actions, and mission metadata.
 */
export type MissionConfigMinipayActivateStandalone = {
  configs: Array<MissionTargetedConfigMinipayActivateStandalone>
  missionType: 'minipay_activate_standalone'
  /**
   * Number of days before an IN_PROGRESS mission times out and is marked as FAILED
   */
  activationTimeoutDays?: number
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  info?: Translatable
}

export type MissionConfigMinipayActivateSubapp = {
  actions?: {
    [key: string]: Action
  }
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigMinipayActivateSubapp>
  missionType: 'minipay_activate_subapp'
}

/**
 * General configuration for polls mission.
 * Defines UI elements, actions, and mission metadata.
 */
export type MissionConfigPolls = {
  actions?: {
    [key: string]: Action
  }
  configs: Array<MissionTargetedConfigPolls>
  missionType?: 'polls'
  iconUrl?: string
  name?: Translatable
  description?: Translatable
}

export type MissionConfigReadArticles = {
  actions?: {
    [key: string]: Action
  }
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number | null
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigReadArticles>
  missionType: 'read_articles'
}

export type MissionConfigResponse = {
  /**
   * Empty list may indicate that mission is disabled if returned together with mission_enabled = False.
   */
  thresholdsKb: Array<number>
  /**
   * Indicates if mission is enabled.
   */
  missionEnabled: boolean
}

export type MissionConfigSelfVerify = {
  configs: Array<MissionTargetedConfigPointsReward>
  missionType: 'self_verify'
}

export type MissionConfigSetWallpaper = {
  actions?: {
    [key: string]: Action
  }
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigSetWallpaper>
  missionType: 'set_wallpaper'
}

export type MissionConfigSignin = {
  actions?: Action
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigPointsReward>
  missionType: 'signin'
}

/**
 * Configuration for a mission that rewards users for maintaining daily activity streaks.
 * The reward amount increases based on the length of the streak through configurable multipliers.
 */
export type MissionConfigStreaks = {
  actions?: {
    [key: string]: Action
  } | null
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number | null
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigStreaks>
  missionType: 'streaks'
  missedCheckinReminders?: MissionConfigStreaksMissedCheckinReminders | null
  /**
   *
   * When set to e.g. 4, it shifts the date for streaks by 4 hours forward,
   * so that the streaks are calculated based on 4am instead of midnight.
   *
   * NOTE: client only supports shift by full hours between 0 - 23.
   *
   * Client validation logic:
   * https://gitlab.service.osa/mobileservices/mobilemissions/clientsdk/-/blob/5e6d7de9af852281b8273ad040e378b7b8642970/lib/core/src/commonMain/kotlin/com/opera/momi/clientsdk/Mission.kt#L122
   *
   */
  midnightShiftHours?: number
  /**
   *
   * The time duration before the end of the day (EOD) during which the
   * cool-off mechanism is active.
   *
   * Example: If this has a value of 1 hour, only users who check in between
   * 23:00 and 23:59 will have the cool-off applied.
   *
   */
  cooloffAppliesBeforeEod?: string
  /**
   *
   * The time that must pass between two consecutive streak check-ins.
   *
   * This mechanism prevents users from registering multiple check-ins too
   * close together, particularly around midnight. For example, if a user
   * checks in at 23:59 on Day 1 and the cool-off minimum interval is 6 hours,
   * their next valid check-in cannot occur before 05:59 on Day 2.
   *
   * NOTE: only applies to users who did checkin within applicable period.
   * (Check `cooloff_applies_before_eod`).
   *
   */
  cooloffInterval?: string
}

export type MissionConfigStreaksLevel = {
  /**
   * Min. streak length when given streaks level becomes active.
   */
  minStreakLength: number
  /**
   * List of rewards granted starting the day the level becomes active.
   */
  rewards: Array<PointsRewardConfig | CryptoTokenRewardConfig>
}

/**
 * Config subobject for notifications for users who missed their streaks.
 * The client types config is applied globally, across all countries.
 */
export type MissionConfigStreaksMissedCheckinReminders = {
  regularRemindersDaysToLastCheckin: Array<number>
  showFinalReminder: boolean
  clientTypes: Array<ClientType>
}

export type MissionConfigTraffic = {
  actions?:
    | {
        [key: string]: Action
      }
    | Action
    | null
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigTraffic>
  missionType: 'traffic'
}

export type MissionConfigWatchAds = {
  actions?: {
    [key: string]: Action
  }
  iconUrl?: string
  name?: Translatable
  description?: Translatable
  step?: number
  limit?: number | null
  periodicity?: MissionPeriodicity
  configs: Array<MissionTargetedConfigWatchAds>
  missionType: 'watch_ads'
}

export type MissionConfigsSchema = {
  configs: Array<
    | MissionConfigGiveaway
    | MissionConfigStreaks
    | MissionConfigDataSaved
    | MissionConfigSetWallpaper
    | MissionConfigGoogleSignin
    | MissionConfigSignin
    | MissionConfigMakeBrowserDefault
    | MissionConfigMinipayActivateSubapp
    | MissionConfigMinipayActivateStandalone
    | MissionConfigEAYB
    | MissionConfigReadArticles
    | MissionConfigPolls
    | MissionConfigWatchAds
    | MissionConfigSelfVerify
    | MissionConfigTraffic
  >
}

export type MissionEntryCounterV2 = {
  type: MissionType | MissionEntryTypeFake
  iconUrl: string
  name: Translatable | string
  description: Translatable | string
  info?: Translatable | string | null
  reward: Amount | null
  boost: MissionBoostStatus | null
  canBeBoosted: boolean
  status: MissionStatus
  action?: Action | null
  variant?: 'counter'
  periodicity: MissionPeriodicity
  counter: CounterState
  extra?: MissionExtraStreaksV2 | MissionExtraPeriodic | MissionExtraPolls | null
  readonly supportsHistory: boolean
}

export type MissionEntryCounterV3 = {
  type: MissionType | MissionEntryTypeFake
  iconUrl: string
  name: Translatable | string
  description: Translatable | string
  info?: Translatable | string | null
  rewards: Array<CurrencyAmount> | null
  boost: MissionBoostStatus | null
  canBeBoosted: boolean
  status: MissionStatus
  action?: Action | null
  variant?: 'counter'
  periodicity: MissionPeriodicity
  counter: CounterState
  /**
   * Mission identifier.
   */
  id: string
  extra?: MissionExtraStreaksV3 | MissionExtraPeriodic | MissionExtraPolls | null
  readonly supportsHistory: boolean
}

export enum MissionEntryTypeFake {
  DEBUG_VIEW_ENTRYPOINT = 'debug_view_entrypoint'
}

export type MissionEntryWebV2 = {
  type: MissionType | MissionEntryTypeFake
  iconUrl: string
  name: Translatable | string
  description: Translatable | string
  info?: Translatable | string | null
  reward: Amount | null
  boost: MissionBoostStatus | null
  canBeBoosted: boolean
  status: MissionStatus
  action?: Action | null
  variant?: 'web'
  extra?: MissionExtraStreaksV2 | MissionExtraPeriodic | MissionExtraPolls | null
  readonly supportsHistory: boolean
}

export type MissionEntryWebV3 = {
  type: MissionType | MissionEntryTypeFake
  iconUrl: string
  name: Translatable | string
  description: Translatable | string
  info?: Translatable | string | null
  rewards: Array<CurrencyAmount> | null
  boost: MissionBoostStatus | null
  canBeBoosted: boolean
  status: MissionStatus
  action?: Action | null
  variant?: 'web'
  /**
   * Mission identifier.
   */
  id: string
  extra?: MissionExtraStreaksV3 | MissionExtraPeriodic | MissionExtraPolls | null
  readonly supportsHistory: boolean
}

/**
 * Base extra for periodic mission.
 */
export type MissionExtraPeriodic = {
  /**
   *
   * Configures an hour at which the periodic missions are reset on the client.
   * For example, if `midnightShift` is `0`, the mission is reset at midnight (00:00),
   * if `midnightShift` is `4` , the mission is reset at 4:00.
   *
   * Related code on the client:
   * https://gitlab.service.osa/mobileservices/mobilemissions/clientsdk/-/blob/master/lib/core/src/commonMain/kotlin/com/opera/momi/clientsdk/Mission.kt#L120
   *
   */
  midnightShift: number
}

/**
 * Extra data for Polls mission containing daily limits.
 */
export type MissionExtraPolls = {
  /**
   * Maximum points that can be earned from polls per day.
   */
  dailyPointsLimit: number
}

export type MissionExtraStreaksV2 = {
  /**
   *
   * Configures an hour at which the periodic missions are reset on the client.
   * For example, if `midnightShift` is `0`, the mission is reset at midnight (00:00),
   * if `midnightShift` is `4` , the mission is reset at 4:00.
   *
   * Related code on the client:
   * https://gitlab.service.osa/mobileservices/mobilemissions/clientsdk/-/blob/master/lib/core/src/commonMain/kotlin/com/opera/momi/clientsdk/Mission.kt#L120
   *
   */
  midnightShift: number
  levels: Array<MissionStreakLevelV2>
}

export type MissionExtraStreaksV3 = {
  /**
   *
   * Configures an hour at which the periodic missions are reset on the client.
   * For example, if `midnightShift` is `0`, the mission is reset at midnight (00:00),
   * if `midnightShift` is `4` , the mission is reset at 4:00.
   *
   * Related code on the client:
   * https://gitlab.service.osa/mobileservices/mobilemissions/clientsdk/-/blob/master/lib/core/src/commonMain/kotlin/com/opera/momi/clientsdk/Mission.kt#L120
   *
   */
  midnightShift: number
  levels: Array<MissionStreakLevelV3>
}

/**
 * Single day entry in mission history.
 */
export type MissionHistoryItem = {
  /**
   * Useful for debugging.
   */
  day: string
  /**
   * 00:00 of the day in user timezone, represented as UTC epoch ms. Duplicates `day` field, follows client convention to operate on unix timestamps.
   */
  timestamp: number
  /**
   * Reward earned on this day, or null if no reward was earned.
   */
  reward: Amount | null
}

export type MissionHistoryNotEnabledForMissionTypeApiErrorBody = {
  detail: string
  errorCode: 'user_mission_type_does_not_support_history'
}

export type MissionHistoryNotEnabledForUserApiErrorBody = {
  detail: string
  errorCode: 'user_mission_history_not_enabled'
}

/**
 * Response containing mission history for the last 7 days.
 */
export type MissionHistoryResponse = {
  /**
   * History items ordered oldest first, always exactly 7 items.
   */
  items: Array<MissionHistoryItem>
}

export enum MissionPeriodicity {
  DAILY = 'daily',
  EVERYOTHERDAY = 'everyotherday',
  NONE = 'none'
}

export enum MissionStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export type MissionStatusesResponse = {
  missionHiddenRewards: HiddenRewardsMissionStatus | null
  missionLoyaltyBonus: null
  missionGiveaway: GiveawayMissionStatus | null
  missionStreaks: StreaksMissionStatus | null
  missionDataSaved: DataSavedMissionStatus | null
}

export type MissionStreakLevelV2 = {
  /**
   * Streak size that activates given level.
   */
  minStreakLength: number
  /**
   * Reward(s) assigned to user on check-ins once the level is achieved.
   */
  reward: Amount | null
}

export type MissionStreakLevelV3 = {
  /**
   * Streak size that activates given level.
   */
  minStreakLength: number
  rewards: Array<CurrencyAmount>
}

export type MissionTargetedConfigDataSaved = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * The ascending values of saved data thresholds (in kB) which should trigger client to report to the BE.
   */
  thresholds: Array<number>
  /**
   * The client will refresh the config according to this value
   */
  ttl?: string
  reward: PointsRewardConfig
}

export type MissionTargetedConfigEAYB = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  boostValues?: Array<BoostValueConfig>
  reward: PointsRewardThresholdsConfig
}

export type MissionTargetedConfigGiveaway = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * If set, mission is only visible for AppInstance.installation_time at or after this time
   */
  minInstallationTime?: string | null
}

export type MissionTargetedConfigGoogleSignin = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
}

export type MissionTargetedConfigMakeBrowserDefault = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
}

export type MissionTargetedConfigMinipayActivateStandalone = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
}

export type MissionTargetedConfigMinipayActivateSubapp = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
  /**
   * Mission Launch date for given target. Users with account active on that date won't be eligible to di the mission.
   */
  launchDate: string
}

export type MissionTargetedConfigPointsReward = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
}

/**
 * Targeted configuration for polls mission.
 * Defines country/client-specific settings and rewards.
 */
export type MissionTargetedConfigPolls = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
  maxWinsPerDay?: number
}

export type MissionTargetedConfigReadArticles = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  boostValues?: Array<BoostValueConfig>
  reward: PointsRewardThresholdsConfig
}

export type MissionTargetedConfigSetWallpaper = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
}

export type MissionTargetedConfigStreaks = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * List of levels that activate at different streak lengths
   */
  levels: Array<MissionConfigStreaksLevel>
  /**
   * List of base rewards granted for check-in (when levels are not active).
   */
  baseRewards: Array<PointsRewardConfig | CryptoTokenRewardConfig>
}

export type MissionTargetedConfigTraffic = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  /**
   * Reward granted on mission completion
   */
  reward: PointsRewardConfig
  targetUrl: string
  slug: string
  periodicity: TrafficPeriodicity
}

export type MissionTargetedConfigWatchAds = {
  /**
   * List of country codes for targeting. Mutually exclusive with country_code.
   */
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom?: string | null
  validTo?: string | null
  enabled?: boolean
  /**
   * Defines which Feature Flag must be enabled to access this mission.
   */
  featureFlag?: FeatureFlag | null
  /**
   * Can be a single ClientVersion string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  minimalVersion?: string | null
  /**
   * Can be a single client version string x, e.g. `99.6.2254.2121`. Defaults to None which means any version.
   */
  maximalVersion?: string | null
  /**
   * Minimum SDK version. A missing SDK version does not satisfy this bound.
   */
  minimalSdkVersion?: string | null
  /**
   * Maximum SDK version. A missing SDK version satisfies this bound.
   */
  maximalSdkVersion?: string | null
  boostValues?: Array<BoostValueConfig>
  /**
   * Points reward granted per ad watched
   */
  reward: PointsRewardConfig
  /**
   * Maximum number of ads that can be rewarded per day
   */
  dailyAdsLimit: number
}

/**
 * NOTE: Any modification of this enum will need alembic migration.
 * If you'll migrate it using the `op.sync_enum_values` it's going to drop all
 * constraints related to the `mission_type`. Remember to recreate them.
 * Check 0054_add_mission_type_giveaway_brazil_free_data.py for reference.
 */
export enum MissionType {
  HIDDEN_REWARDS = 'hidden_rewards',
  HIDDEN_REWARDS_SMALL_REWARD = 'hidden_rewards_small_reward',
  LOYALTY_BONUS = 'loyalty_bonus',
  STREAKS = 'streaks',
  GIVEAWAY = 'giveaway',
  DATA_SAVED = 'data_saved',
  ROULETTE = 'roulette',
  SET_WALLPAPER = 'set_wallpaper',
  EAYB = 'eayb',
  GOOGLE_SIGNIN = 'google_signin',
  SIGNIN = 'signin',
  MAKE_BROWSER_DEFAULT = 'make_browser_default',
  OPEN_ARTICLE = 'open_article',
  READ_ARTICLES = 'read_articles',
  POLLS = 'polls',
  MINIPAY_ACTIVATE_SUBAPP = 'minipay_activate_subapp',
  MINIPAY_ACTIVATE_STANDALONE = 'minipay_activate_standalone',
  WATCH_ADS = 'watch_ads',
  SELF_VERIFY = 'self_verify',
  TRAFFIC = 'traffic'
}

export type MissionsResponseV2 = {
  items: Array<MissionEntryWebV2 | MissionEntryCounterV2>
}

export type MissionsResponseV3 = {
  items: Array<MissionEntryWebV3 | MissionEntryCounterV3>
}

export type MonitoredWalletBalanceResponse = {
  /**
   * Human-readable configured wallet name.
   */
  name: string
  /**
   * Canonical wallet address.
   */
  address: string
  /**
   * Configured token.
   */
  token: CryptoTokenType
  /**
   * Live balance in whole-token units; null when unavailable.
   */
  balance: string | null
  /**
   * Configured alert threshold in whole-token units.
   */
  alertThreshold: string
  /**
   * Whether a low balance triggers an alert.
   */
  alertEnabled: boolean
  /**
   * Current threshold and availability status.
   */
  status: MonitoredWalletBalanceStatus
}

export enum MonitoredWalletBalanceStatus {
  HEALTHY = 'healthy',
  LOW = 'low',
  UNAVAILABLE = 'unavailable'
}

export type MonitoredWalletBalancesResponse = {
  items: Array<MonitoredWalletBalanceResponse>
}

export type NotFoundApiErrorBody = {
  detail: string
  errorCode: 'not_found'
}

export type NotificationLogSchema = {
  id: number
  messageTypeId: string | null
  userFcmTokenId: number | null
  appInstanceId: number | null
  fcmMessageId: string | null
  pusherJobId: string | null
  huid: string | null
  status: NotificationStatus
  errorMessage: string | null
  createdAt: string
}

export type NotificationLogsResponse = {
  items?: Array<NotificationLogSchema>
}

export enum NotificationStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

/**
 * Derived financial metrics for one occurrence.
 *
 * Every amount is an integer in the campaign token's smallest unit.
 */
export type OccurrenceReportFinancialsSchema = {
  /**
   * Total amount already committed to successful earnings, in the token's smallest unit.
   */
  committedAmount: number
  /**
   * Amount still available for future earnings, in the token's smallest unit.
   */
  remainingAmount: number
  /**
   * Number of complete earnings the remaining amount can still fund.
   */
  remainingEarningCount: number
  /**
   * True when the remaining amount cannot fund another complete earning. An indivisible remainder may still be non-zero.
   */
  isExhausted: boolean
}

/**
 * One occurrence report entry with schedule and materialized state.
 */
export type OccurrenceReportItemSchema = {
  /**
   * Inclusive UTC start of this occurrence's schedule window.
   */
  startsAt: string
  /**
   * Exclusive UTC end of this occurrence's schedule window.
   */
  endsAt: string
  /**
   * Number of successful earnings committed against this occurrence. Zero when the occurrence has not been materialized.
   */
  successfulEarningCount: number
  /**
   * Derived financial metrics for this occurrence.
   */
  financials: OccurrenceReportFinancialsSchema
}

/**
 * Paginated occurrence-report response, newest-first.
 */
export type OccurrenceReportListResponse = {
  items: Array<OccurrenceReportItemSchema>
  total: number
  limit: number
  offset: number
}

export type OfferLimitMessage = {
  title: Translatable
  message: Translatable
}

export type OfferLimitResponse = {
  message: OfferLimitMessage | null
  limitReached: boolean
}

/**
 * Generic response schema for any market offer.
 */
export type OfferSchema = {
  id: number
  /**
   * Will be i.e. icon_id or wallpaper_id based on product type
   */
  itemId: number
  price: number
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  purchaseLimitWeekly: number | null
  purchaseLimitMonthly: number | null
  purchaseLimitTotal: number | null
  purchaseLimitAppInstanceWeekly: number | null
  startDate: string | null
  endDate: string | null
  targetingId: number | null
  createdAt: string
  updatedAt: string
}

/**
 * Persona bundles of :class:`Permission`.
 *
 * Roles are environment-blind: the `operator` table is per-environment, so
 * the same human may hold `ADMIN` in dev (to test anything) and a restricted
 * role (or no row) in prod. Assignment policy, not code, handles that.
 */
export enum OperatorRole {
  NONE = 'none',
  ADMIN = 'admin',
  QA = 'qa',
  BUSINESS = 'business'
}

export type PayloadFormGiveaway = {
  email: string
  phoneNumber: string
  rewardChoice: GiveawayRewardChoice
}

/**
 * Keyset-paginated list of pending (not yet distributed) rewards.
 */
export type PendingRewardListResponse = {
  items: Array<RewardItem>
  /**
   * Cursor for next page (min id). None when no more pages.
   */
  next_cursor: number | null
  /**
   * Page size used for this response
   */
  size: number
}

/**
 * Atomic admin capability. Each protected admin route requires one.
 */
export enum Permission {
  USER_VIEW = 'user_view',
  USER_MANAGE = 'user_manage',
  USER_TOKEN_ISSUE = 'user_token_issue',
  TARGETING_VIEW = 'targeting_view',
  TARGETING_MANAGE = 'targeting_manage',
  CACHE_VIEW = 'cache_view',
  CACHE_MANAGE = 'cache_manage',
  MARKET_VIEW = 'market_view',
  MARKET_MANAGE = 'market_manage',
  COUPON_CAMPAIGN_VIEW = 'coupon_campaign_view',
  COUPON_CAMPAIGN_MANAGE = 'coupon_campaign_manage',
  TOKEN_PAYOUT_VIEW = 'token_payout_view',
  TOKEN_PAYOUT_MANAGE = 'token_payout_manage',
  CHAIN_CLAIM_VIEW = 'chain_claim_view',
  CHAIN_CLAIM_MANAGE = 'chain_claim_manage',
  TASK_EXECUTION_VIEW = 'task_execution_view',
  CRYPTO_VIEW = 'crypto_view',
  CELO_VIEW = 'celo_view',
  TOP_EARNERS_VIEW = 'top_earners_view',
  REWARDS_REPOSITORY_VIEW = 'rewards_repository_view',
  SPOTIFY_VOUCHER_VIEW = 'spotify_voucher_view',
  MISSION_VIEW = 'mission_view',
  CUSTOM_FORM_VIEW = 'custom_form_view',
  REDEEM_FLOW_VIEW = 'redeem_flow_view',
  LEADERBOARD_VIEW = 'leaderboard_view',
  BONUS_CAMPAIGNS_VIEW = 'bonus_campaigns_view',
  BONUS_CAMPAIGNS_MANAGE = 'bonus_campaigns_manage',
  TRAFFIC_CAMPAIGN_VIEW = 'traffic_campaign_view',
  TRAFFIC_CAMPAIGN_MANAGE = 'traffic_campaign_manage'
}

export type PhoneNumberNotRegisteredApiErrorBody = {
  detail: string
  errorCode: 'market_usdt_phone_not_registered'
}

/**
 * Response for phone number validation endpoint.
 *
 * Returns whether the phone number is registered with MiniPay.
 */
export type PhoneNumberValidationResponse = {
  /**
   * The validated phone number in E.164 format
   */
  phoneNumber: string
  /**
   * Whether the phone number is registered with MiniPay
   */
  isMinipayRegistered: boolean
}

export type PointsDetails = {
  balance: number
  /**
   * None for backward compatibility.
   */
  spinByPoints?: null
}

export type PointsEarnerSchema = {
  appInstanceId: number
  pointsEarned: number
  countryCode: string
  clientType: ClientType
}

export type PointsHistoryRecord = {
  pointsLogId: number
  description: Translatable
  amount: number
  timestamp: string
  boost?: number | null
  marketItem?: MarketItemReference | null
}

export enum PointsLogReason {
  EXCHANGE_TO_CASH = 'exchange_to_cash',
  EXCHANGE_TO_SPIN = 'exchange_to_spin',
  MANUALLY_ADDED = 'manually_added',
  MISSION_ROULETTE = 'mission_roulette',
  MISSION_DATA_SAVED = 'mission_data_saved',
  MISSION_STREAKS = 'mission_streaks',
  PURCHASE_WALLPAPER = 'purchase_wallpaper',
  PURCHASE_ICON = 'purchase_icon',
  PURCHASE_USDT = 'purchase_usdt',
  PURCHASE_TOKEN = 'purchase_token',
  MISSION_SET_WALLPAPER = 'mission_set_wallpaper',
  EAYB = 'eayb',
  MISSION_GOOGLE_SIGNIN = 'mission_google_signin',
  MISSION_SIGNIN = 'mission_signin',
  MISSION_MAKE_BROWSER_DEFAULT = 'mission_make_browser_default',
  MISSION_OPEN_ARTICLE = 'mission_open_article',
  MISSION_READ_ARTICLES = 'mission_read_articles',
  MISSION_POLLS = 'mission_polls',
  MISSION_MINIPAY_ACTIVATE_SUBAPP = 'mission_minipay_activate_subapp',
  MISSION_WATCH_ADS = 'mission_watch_ads',
  MISSION_SELF_VERIFY = 'mission_self_verify',
  MISSION_TRAFFIC = 'mission_traffic',
  PURCHASE_SPOTIFY_VOUCHER = 'purchase_spotify_voucher',
  COUPON_PURCHASE = 'coupon_purchase',
  EAYB_AGGREGATE = 'eayb_aggregate',
  MISSION_OPEN_ARTICLE_AGGREGATE = 'mission_open_article_aggregate',
  MISSION_READ_ARTICLES_AGGREGATE = 'mission_read_articles_aggregate',
  MISSION_WATCH_ADS_AGGREGATE = 'mission_watch_ads_aggregate'
}

export type PointsLogResponse = {
  items: Array<PointsHistoryRecord>
  balance: number
}

export type PointsNotEnabledForUserApiErrorBody = {
  detail: string
  errorCode: 'user_points_not_enabled'
}

export type PointsRewardConfig = {
  kind?: 'points'
  amount: number
  reason: PointsLogReason
  /**
   *
   * Indicates if points for this assignable reward should be selected
   * from inventory or we allow to giveaway points without limits.
   *
   */
  useInventory: boolean
}

export type PointsRewardThresholdsConfig = {
  thresholds: Array<ThresholdConfig>
  reason: PointsLogReason
}

export enum ProblemArea {
  VERIFICATION = 'verification',
  PRIZES = 'prizes',
  MISSIONS = 'missions',
  OTHERS = 'others'
}

/**
 * Request payload for processing match results.
 *
 * This endpoint is called by external services to trigger processing
 * of poll results for a specific match.
 */
export type ProcessMatchResultsRequest = {
  /**
   * Integer ID used to identify the match
   */
  matchId: number
  /**
   * Start time for betting in ISO 8601 format without timezone (e.g., '2024-01-15T14:30:00')
   */
  bettingStartDatetime: string
  /**
   * The correct bet result: 'win', 'lose', or 'draw'
   */
  correctBet: BetOption
}

/**
 * Response from processing match results.
 */
export type ProcessMatchResultsResponse = {
  /**
   * The match ID that is being processed
   */
  matchId: number
  /**
   * The Celery task ID for debugging eventual failures
   */
  taskId: string
}

export enum ProductId {
  PREMIUM_WALLPAPER = 'premium-wallpaper',
  PREMIUM_ICON = 'premium-icon',
  USDT = 'usdt',
  TOKENS = 'tokens',
  SPOTIFY_VOUCHER = 'spotify-voucher',
  COUPON_CAMPAIGNS = 'coupon-campaigns'
}

/**
 * ProductItem is entry returned by /products endpoint.
 * This endpoint shows cheapest Product from every category.
 * It's heavily UI-related endpoint.
 */
export type ProductItem = {
  /**
   * Unique identifier of product
   */
  id: ProductId | string
  iconUrl: string
  title: Translatable
  action: Action
  /**
   * null indicates that price should not be shown
   */
  price: Amount | null
}

export type ProductsResponse = {
  items: Array<ProductItem>
}

export type PurchaseIntentRequest = {
  /**
   * Market product type (e.g. 'premium-wallpaper', 'premium-icon').
   */
  productType: ProductId
  /**
   * Identifier of the specific item within the given product type.
   */
  itemId: number
}

export type PurchaseIntentResponse = {
  /**
   * Unique identifier for this payment intent.
   */
  intentId: string
  /**
   * Celo wallet address the user must send the payment to.
   */
  treasuryAddress: string
  /**
   * Amount in Wei the user must send (serialized as string to avoid JS precision loss).
   */
  amountWei: string
  /**
   * UTC timestamp after which this intent is no longer valid.
   */
  expiresAt: string
}

export type PurchaseLimitExceededApiErrorBody = {
  detail: string
  errorCode: 'market_purchase_limit_exceeded'
}

export type PusherInAppCenterDialog = {
  id: string
  title: string
  body: string
  primary_text: string
  secondary_text?: string | null
  primary_action_url: string
  secondary_action_url?: string | null
  badge_image?: string | null
  center_image?: string | null
}

export type PusherPushMessage = {
  id: string
  body: string
  target_url: string
  title?: string | null
  image?: string | null
}

export enum RateLimitKind {
  MINUTE = 'minute',
  DAILY = 'daily'
}

export type RedeemFlowClaimFormEntry = {
  assignableRewardItemId: number
  claimFormV2EntryId: number
  variant: AssignableRewardTypeVariant
  amount: string
  countryCode: string
  /**
   * HAID the reward was assigned to.
   */
  assignedHaid: string
  /**
   * App Instance ID the reward was assigned to.
   */
  assignedAppInstanceId: number
  /**
   * When the reward was assigned.
   */
  assignedAt: string
  /**
   * When the reward was claimed.
   */
  claimedAt: string
  /**
   * User response
   */
  submittedContent: {
    [key: string]: unknown
  }
}

export type RedeemFlowClaimFormResponse = {
  items: Array<RedeemFlowClaimFormEntry>
  total: number
}

export type ResponseRewardStatusGiveaway = {
  status: RewardStatusGiveaway
  rewardChoice: GiveawayRewardChoice
  rewardChoiceConfig: RewardChoiceConfig
  failureReason: MinuRewardStatusError | null
  voucher: VoucherData | null
}

export type RewardChoiceConfig = {
  choice: GiveawayRewardChoice
  name: Translatable
  /**
   * Description shown when user claimed that reward
   */
  claimedDescription: Translatable
  /**
   * Title of a card representing this reward choice
   */
  cardTitle: Translatable
  /**
   * Subtitle of a card representing this reward choice
   */
  cardSubtitle: Translatable
  brandName: Translatable
  imageUrl: InlineTranslatable
  brandUrl: string
}

export enum RewardCurrency {
  POINTS = 'points',
  CELO = 'celo',
  USDT = 'usdt',
  USDM = 'usdm',
  USDC = 'usdc'
}

/**
 * Single reward distribution result from Crypto Beijing.
 */
export type RewardDistributedItem = {
  /**
   * Reward record ID that was distributed
   */
  id: number
  /**
   * User wallet address
   */
  wallet_address: string
  /**
   * Reward amount. Provided by caller. Not used by API endpoint.
   */
  amount: number | string
  /**
   * Blockchain transaction hash
   */
  tx_hash: string
}

/**
 * Response for distribution callback.
 */
export type RewardDistributedResponse = {
  /**
   * Number of records processed
   */
  processed: number
}

export enum RewardDistributionQueue {
  REWARDS = 'rewards',
  CLAIM_GAS = 'claim_gas'
}

export type RewardDistributionRegistriesResponse = {
  items?: Array<RewardDistributionRegistrySchema>
}

export type RewardDistributionRegistrySchema = {
  id: number
  amount: string
  walletAddress: string
  appInstanceId: number | null
  txHash: string | null
  status: RewardDistributionStatus
  tokenType: CryptoTokenType
  reason: TokenPayoutReason
  cryptoTokenEarnLogId: number | null
  createdAt: string
  updatedAt: string
}

export enum RewardDistributionStatus {
  PENDING = 'PENDING',
  PAYOUTED = 'PAYOUTED'
}

/**
 * Single reward entry for distribution.
 */
export type RewardItem = {
  /**
   * Unique reward record ID
   */
  id: number
  /**
   * Reward amount (1.00 = 1 $, up to 9 decimal places). Always serialized in fixed-point notation (never scientific), e.g. 0.000000001 instead of 1e-09.
   */
  amount: string
  /**
   * User wallet address
   */
  wallet_address: string
  token_type: CryptoTokenType
}

export enum RewardStatusGiveaway {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE'
}

export type ScheduleNotificationRequest = {
  userMissionId: number
  missionType: string
  pointsAmount: number
  scheduledAt: string
  applySpreadDelay?: boolean
}

export type ScheduleNotificationResponse = {
  success: boolean
  message: string
  scheduledNotification?: ScheduledNotificationSchema | null
}

export type ScheduledNotificationSchema = {
  userMissionId: number
  missionType: string
  pointsAmount: number
  scheduledAt: string
}

export type ScheduledNotificationsResponse = {
  items?: Array<ScheduledNotificationSchema>
}

export type SendFcmMessageRequest = {
  id: string
  title: string
  message?: string | null
  link?: string | null
  notificationType?: 'system_notification' | 'in_app' | 'hint' | 'toast' | 'streak_claim' | null
  inappButtonText?: string | null
  inappButton2Text?: string | null
  inappButtonLink?: string | null
  inappButton2Link?: string | null
  inappMessage?: string | null
  inappReward?: string | null
  inappImg?: string | null
  inappBigImg?: string | null
  fallbackToSystem?: boolean | null
  timeToLive?: string | null
  dryRun?: boolean
}

export type SendFcmMessageResponse = {
  success: boolean
  error: string | null
  successCount?: number
  failureCount?: number
  totalTokens?: number
  messageIds?: Array<string>
  responseErrors?: Array<string | null>
}

export type SendPusherMessageRequest = {
  message: PusherInAppCenterDialog | PusherPushMessage
  dryRun?: boolean
}

export type SendPusherMessageResponse = {
  success: boolean
  jobId?: string | null
  error?: string | null
}

export type SignatureResponseClaimed = {
  /**
   * Whether the server has any record of issuing this signature
   */
  exists: boolean
  /**
   * Associated idempotency key (0 when exists=false)
   */
  claimId: number
  /**
   * Signature expiry in unix seconds (0 when exists=false)
   */
  expiration: string
  status: 2
  txHash: string
  prepaidGas: string
  gasFeePaid: string
}

export type SignatureResponseUnclaimed = {
  /**
   * Whether the server has any record of issuing this signature
   */
  exists: boolean
  /**
   * Associated idempotency key (0 when exists=false)
   */
  claimId: number
  /**
   * Signature expiry in unix seconds (0 when exists=false)
   */
  expiration: string
  status: 0 | 1
}

export type SpotifyMarketInfoSchema = {
  isAvailable: boolean
  isPurchased?: boolean | null
}

export type SpotifyOfferCardExtra = {
  badge: Translatable
  heading: Translatable
  body: Translatable
  termsUrl: string
}

export type SpotifyVoucherAlreadyPurchasedApiErrorBody = {
  detail: string
  errorCode: 'market_spotify_voucher_already_purchased'
}

export type SpotifyVoucherFeatureDisabledApiErrorBody = {
  detail: string
  errorCode: 'market_spotify_voucher_feature_disabled'
}

/**
 * Single Spotify voucher item (config-driven, not DB-backed).
 */
export type SpotifyVoucherItem = {
  /**
   * Mock id — Spotify voucher is config-driven and has no database row. Always 0. Included because the client expects every market item to have an id.
   */
  id?: 0
  price: Amount
  state: MarketItemState
  offerHash: string
  extra?: SpotifyVoucherItemExtra | null
}

export type SpotifyVoucherItemExtra = {
  title: Translatable
  offers: Array<SpotifyOfferCardExtra>
}

export type SpotifyVoucherItemsResponse = {
  items: Array<SpotifyVoucherItem>
}

export type SpotifyVoucherNoOperaUserIdApiErrorBody = {
  detail: string
  errorCode: 'market_spotify_voucher_no_opera_user_id'
}

export type SpotifyVoucherNotAvailableApiErrorBody = {
  detail: string
  errorCode: 'market_spotify_voucher_not_available'
}

export type SpotifyVoucherNotPurchasedApiErrorBody = {
  detail: string
  errorCode: 'market_spotify_voucher_not_purchased'
}

export type SpotifyVoucherPromoServiceApiErrorBody = {
  detail: string
  errorCode: 'market_spotify_voucher_promo_service_error'
}

/**
 * Purchase request for the Spotify voucher.
 */
export type SpotifyVoucherPurchaseRequest = {
  /**
   * Mock id — ignored server-side. Included because the client sends id for every market purchase request.
   */
  id?: number
  offerHash: string
}

/**
 * Response for purchase and redemption-detail endpoints.
 */
export type SpotifyVoucherPurchaseResponse = {
  code: string
  targetUrl: string
}

export type SpotifyVoucherStockSchema = {
  availableCodes: number
}

export enum Status {
  OK = 'OK',
  ERROR_NO_DB_CONNECTION = 'ERROR_NO_DB_CONNECTION'
}

export type StatusOkResponse = {
  status?: 'OK'
}

export type StreaksCheckInAttemptDuringActiveCoolOffApiErrorBody = {
  detail: string
  errorCode: 'streaks_checkin_attempt_during_active_cool_off'
  extra: CounterMissionRejectedReportResponse
}

export type StreaksCheckInMissionIsNotEnabledForUserApiErrorBody = {
  detail: string
  errorCode: 'streaks_checkin_mission_is_not_enabled_for_user'
}

export type StreaksMissionStatus = {
  currentStreakLength: number
  /**
   * None indicates that there was no previous streak
   */
  previousStreakLastCheckInDate: string | null
}

export type StreamsSummary = {
  pending?: number
  completed?: number
  failed?: number
  expired?: number
}

export enum SupplyMethod {
  BATCH = 'BATCH',
  PARTNER_API = 'PARTNER_API',
  NONE = 'NONE'
}

export type SyncRunDetailSchema = {
  run: SyncRunSchema
  streams: Array<SyncStreamSchema>
}

export type SyncRunListResponse = {
  items: Array<SyncRunSchema>
  total: number
  limit: number
  offset: number
}

export type SyncRunSchema = {
  id: number
  status: SyncRunStatus
  queryDate: string
  startedAt: string
  finishedAt: string | null
  statsInserted: number | null
  statsUpdated: number | null
  statsSkipped: number | null
  statsDeleted: number | null
  errorMessage: string | null
  streamsSummary: StreamsSummary
}

export enum SyncRunStatus {
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export type SyncStreamSchema = {
  id: number
  streamName: string | null
  status: SyncStreamStatus
  statsInserted: number | null
  statsUpdated: number | null
  statsSkipped: number | null
  errorMessage: string | null
}

export enum SyncStreamStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED'
}

export type SyncTargetingsRequest = {
  queryDate: string
}

export type SyncTargetingsResponse = {
  taskId: string
  queryDate: string
}

export type SyncTargetingsResult = {
  queryDate: string
  error: string | null
}

export type SyncTargetingsTaskStatus = {
  taskId: string
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE' | 'RETRY' | 'REVOKED' | string
  ready: boolean
  successful: boolean | null
  result: SyncTargetingsResult | null
}

export type TargetingDetailSchema = {
  id: number
  slugName: string
  lastSyncedAt: string | null
  appInstanceCount: number
  assignedOfferIds: Array<number>
}

export type TargetingListResponse = {
  items: Array<TargetingSchema>
  total: number
  limit: number
  offset: number
}

export type TargetingSchema = {
  id: number
  slugName: string
  /**
   * Used to break ties when a user matches more than one targeted offer for the same item. Lower value = higher priority. Must be unique across all targetings.
   */
  priority?: number | null
  lastSyncedAt: string | null
  appInstanceCount?: number | null
}

export type TaskExecutionListResponse = {
  total: number
  items: Array<TaskExecutionResponse>
}

export type TaskExecutionResponse = {
  id: number
  taskId: string
  taskName: string
  retryCount: number
  state: TaskExecutionState
  startedAt: string
  finishedAt: string | null
}

/**
 * Execution attempt states matching Celery's task states.
 *
 * `STARTED` is recorded on `task_prerun`; other states map directly from the
 * state reported by `task_postrun`.
 * See [Celery task states](https://docs.celeryq.dev/en/stable/reference/celery.states.html)
 * and [semipredicates](https://docs.celeryq.dev/en/stable/userguide/tasks.html#semipredicates).
 */
export enum TaskExecutionState {
  STARTED = 'STARTED',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  RETRY = 'RETRY',
  IGNORED = 'IGNORED',
  REJECTED = 'REJECTED'
}

export type TelemetryEvent = {
  eventId: string
  eventName: string
  timestamp: string
  payload: string
}

export type TelemetryEventRequest = {
  events: Array<TelemetryEvent>
}

export type ThresholdConfig = {
  threshold?: number
  amount: number
}

export type TierHistoryItemResponse = {
  /**
   * ISO-formatted date. This is for debugging purposes only and should not be relied on.
   */
  date: string
  /**
   * UTC midnight timestamp, in milliseconds, for the day represented by this item.
   */
  timestamp: number
  /**
   * User's tier on that day, or null when the user was not in the leaderboard.
   */
  tier: number | null
  /**
   * Whether this history entry is related to day when rewards were payout
   */
  isPayoutDay: boolean
}

export type TierHistoryResponse = {
  /**
   * History of user's tier for the last 30 days, ordered from oldest to newest.
   */
  items: Array<TierHistoryItemResponse>
}

export type TokenMonthlyLimitReachedApiErrorBody = {
  detail: string
  errorCode: 'market_token_monthly_limit_reached'
}

export type TokenOfferGlobalLimit = {
  /**
   * Total monthly limit
   */
  limit: string
  /**
   * Total spent this month
   */
  spent: string
}

/**
 * Single token offer item (currency-agnostic).
 */
export type TokenOfferItem = {
  /**
   * Unique identifier of the token offer
   */
  id: number
  price: Amount
  state: MarketItemState
  offerHash: string
  extra: TokenOfferItemExtra
}

export type TokenOfferItemExtra = {
  /**
   * Amount of the token in this offer
   */
  amount: string
  /**
   * Token currency identifier
   */
  currency: CryptoTokenType
}

/**
 * Response schema for a token offer's purchase limits and current rolling-window usage.
 */
export type TokenOfferLimitsResponse = {
  offerId: number
  tokenType: MarketTokenType
  tokenAmount: string
  purchaseLimitWeekly: number | null
  purchaseLimitWeeklyUsed: number | null
  purchaseLimitMonthly: number | null
  purchaseLimitMonthlyUsed: number | null
  purchaseLimitTotal: number | null
  purchaseLimitTotalUsed: number | null
  purchaseLimitAppInstanceWeekly: number | null
}

export type TokenOfferNotFoundApiErrorBody = {
  detail: string
  errorCode: 'market_token_offer_not_found'
}

/**
 * Response schema for a token offer.
 */
export type TokenOfferSchema = {
  id: number
  /**
   * Will be i.e. icon_id or wallpaper_id based on product type
   */
  itemId: number
  price: number
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  purchaseLimitWeekly: number | null
  purchaseLimitMonthly: number | null
  purchaseLimitTotal: number | null
  purchaseLimitAppInstanceWeekly: number | null
  startDate: string | null
  endDate: string | null
  targetingId: number | null
  createdAt: string
  updatedAt: string
  tokenType: MarketTokenType
  tokenAmount: string
}

/**
 * Response containing list of token offers.
 */
export type TokenOffersResponse = {
  items: Array<TokenOfferItem>
}

export enum TokenPayoutReason {
  DUSTING = 'dusting',
  CELO_WEEKLY_PAYOUT = 'celo_weekly_payout',
  POINTS_TO_TOKEN = 'points_to_token',
  MANUALLY_ADDED = 'manually_added',
  CRYPTO_TOKEN_CLAIM_GAS_FEE = 'crypto_token_claim_gas_fee',
  LEADERBOARD_PAYOUT = 'leaderboard_payout'
}

export type TokenPayoutRunListResponse = {
  total: number
  items: Array<TokenPayoutRunResponse>
}

export type TokenPayoutRunResponse = {
  id: number
  weekNumber: number
  year: number
  status: TokenPayoutRunStatus
  startedAt: string | null
  finishedAt: string | null
  totalProcessed: number
  totalSkippedNoWallet: number
  totalSkippedDuplicate: number
  totalSkippedNoAppInstance: number
  totalSkippedCountryDisabled: number
  totalSkippedClientTypeCapReached: number
  totalPayoutAmount: string
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

export enum TokenPayoutRunStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

/**
 * Request to purchase a token exchange.
 */
export type TokenPurchaseRequest = {
  /**
   * Item ID
   */
  id: number
  offerHash: string
  /**
   * Phone number for token transfer
   */
  phoneNumber: string
}

/**
 * Response for token exchange purchase.
 */
export type TokenPurchaseResponse = {
  /**
   * ID of the PointsLog entry used to purchase this token exchange. It can be used to link the generated cashlink with points log data in points history view.
   */
  relatedPointsLogId: number
}

export type TokenToRefresh = {
  refreshToken: string
  huid?: string | null
  /**
   * Client version at the moment of token refresh. Useful to inform BE about version change (client update).
   */
  clientVersion?: string | null
  /**
   * Client SDK version at the moment of token refresh. Report a new value after an SDK update; omit it to keep the stored value.
   */
  sdkVersion?: string | null
}

export type Tokens = {
  accessToken: string
  refreshToken: string
}

export type TopAppInstanceItem = {
  appInstanceId: number
  totalPayout: string
  purchaseCount: number
  tokenType: CryptoTokenType
}

export type TopAppInstancesResponse = {
  items: Array<TopAppInstanceItem>
}

export type TopWalletItem = {
  walletAddress: string
  totalPayout: string
  purchaseCount: number
  tokenType: CryptoTokenType
}

export type TopWalletsResponse = {
  items: Array<TopWalletItem>
}

export enum TrafficCampaignAction {
  EDIT = 'edit',
  PUBLISH = 'publish',
  WITHDRAW = 'withdraw',
  EXTEND = 'extend',
  DUPLICATE = 'duplicate',
  DELETE = 'delete'
}

export type TrafficCampaignConflict = {
  id: number
  slug: string
}

export type TrafficCampaignConflictApiErrorBody = {
  detail: string
  errorCode: 'admin_traffic_campaign_conflict'
  extra: TrafficCampaignConflictExtra
}

/**
 * Extra data for `TrafficCampaignConflictApiError`.
 */
export type TrafficCampaignConflictExtra = {
  conflictingCampaigns: Array<TrafficCampaignConflictItem>
}

/**
 * A conflicting campaign reference with id and slug.
 */
export type TrafficCampaignConflictItem = {
  id: number
  slug: string
}

/**
 * Extra data for `TrafficCampaignInvalidConfigurationApiError`.
 */
export type TrafficCampaignInvalidConfigExtra = {
  fields: {
    [key: string]: string
  }
}

export type TrafficCampaignInvalidConfigurationApiErrorBody = {
  detail: string
  errorCode: 'admin_traffic_campaign_invalid_configuration'
  extra: TrafficCampaignInvalidConfigExtra
}

export type TrafficCampaignListItem = {
  slug: string
  targetUrl: string
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom: string
  validTo: string
  rewardAmount: number
  periodicity: TrafficPeriodicity
  featureFlag?: FeatureFlag | null
  minimalVersion?: string | null
  maximalVersion?: string | null
  id: number
  enabled: boolean
  state: TrafficCampaignState
  availableActions?: Array<TrafficCampaignAction>
  createdAt: string
  updatedAt: string
}

export type TrafficCampaignListResponse = {
  items: Array<TrafficCampaignListItem>
  total: number
  limit: number
  offset: number
}

export type TrafficCampaignNotFoundApiErrorBody = {
  detail: string
  errorCode: 'admin_traffic_campaign_not_found'
}

export type TrafficCampaignOperationNotAllowedApiErrorBody = {
  detail: string
  errorCode: 'admin_traffic_campaign_operation_not_allowed'
}

export type TrafficCampaignResponse = {
  slug: string
  targetUrl: string
  countryCodes: Array<string>
  clientTypes: Array<ClientType>
  validFrom: string
  validTo: string
  rewardAmount: number
  periodicity: TrafficPeriodicity
  featureFlag?: FeatureFlag | null
  minimalVersion?: string | null
  maximalVersion?: string | null
  id: number
  enabled: boolean
  state: TrafficCampaignState
  availableActions?: Array<TrafficCampaignAction>
  createdAt: string
  updatedAt: string
  conflicts?: Array<TrafficCampaignConflict>
}

export type TrafficCampaignSlugExistsApiErrorBody = {
  detail: string
  errorCode: 'admin_traffic_campaign_slug_exists'
}

export enum TrafficCampaignState {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  ENDED = 'ended'
}

export enum TrafficPeriodicity {
  DAILY = 'daily',
  NONE = 'none'
}

/**
 * Translatable string that may contain formatting placeholders
 */
export type Translatable = string

export type TriggerTokenPayoutRequest = {
  weekNumber: number
  year: number
}

export type TriggerTokenPayoutResponse = {
  taskId: string
  weekNumber: number
  year: number
}

export type UpdateAppInstanceCommentRequest = {
  comment: string
}

export type UpdateAppInstanceStateRequest = {
  isLoggedIntoGoogle: boolean | null
  isDefaultBrowser: boolean | null
  isLoggedIntoHostApp: boolean | null
  isFresh: boolean | null
  isMinipayInstalled: boolean | null
  isGooglePlayAvailable: boolean | null
  diagnosticInfo?: DiagnosticInfo | null
}

/**
 * Payload to update editable fields on a Draft bonus campaign.
 *
 * All fields are optional. Only supplied fields are updated.
 * A no-op update (all values identical) succeeds without error.
 */
export type UpdateBonusCampaignRequest = {
  /**
   * Internal name shown to operators.
   */
  name?: string | null
  /**
   * Targeting whose current membership determines eligible users.
   */
  targetingId?: number | null
  /**
   * Campaign start as an absolute UTC instant.
   */
  startsAt?: string | null
  /**
   * Exclusive campaign end as an absolute UTC instant.
   */
  endsAt?: string | null
  /**
   * IANA timezone for recurring occurrence boundaries.
   */
  timezone?:
    | 'Africa/Abidjan'
    | 'Africa/Accra'
    | 'Africa/Addis_Ababa'
    | 'Africa/Algiers'
    | 'Africa/Asmara'
    | 'Africa/Asmera'
    | 'Africa/Bamako'
    | 'Africa/Bangui'
    | 'Africa/Banjul'
    | 'Africa/Bissau'
    | 'Africa/Blantyre'
    | 'Africa/Brazzaville'
    | 'Africa/Bujumbura'
    | 'Africa/Cairo'
    | 'Africa/Casablanca'
    | 'Africa/Ceuta'
    | 'Africa/Conakry'
    | 'Africa/Dakar'
    | 'Africa/Dar_es_Salaam'
    | 'Africa/Djibouti'
    | 'Africa/Douala'
    | 'Africa/El_Aaiun'
    | 'Africa/Freetown'
    | 'Africa/Gaborone'
    | 'Africa/Harare'
    | 'Africa/Johannesburg'
    | 'Africa/Juba'
    | 'Africa/Kampala'
    | 'Africa/Khartoum'
    | 'Africa/Kigali'
    | 'Africa/Kinshasa'
    | 'Africa/Lagos'
    | 'Africa/Libreville'
    | 'Africa/Lome'
    | 'Africa/Luanda'
    | 'Africa/Lubumbashi'
    | 'Africa/Lusaka'
    | 'Africa/Malabo'
    | 'Africa/Maputo'
    | 'Africa/Maseru'
    | 'Africa/Mbabane'
    | 'Africa/Mogadishu'
    | 'Africa/Monrovia'
    | 'Africa/Nairobi'
    | 'Africa/Ndjamena'
    | 'Africa/Niamey'
    | 'Africa/Nouakchott'
    | 'Africa/Ouagadougou'
    | 'Africa/Porto-Novo'
    | 'Africa/Sao_Tome'
    | 'Africa/Timbuktu'
    | 'Africa/Tripoli'
    | 'Africa/Tunis'
    | 'Africa/Windhoek'
    | 'America/Adak'
    | 'America/Anchorage'
    | 'America/Anguilla'
    | 'America/Antigua'
    | 'America/Araguaina'
    | 'America/Argentina/Buenos_Aires'
    | 'America/Argentina/Catamarca'
    | 'America/Argentina/ComodRivadavia'
    | 'America/Argentina/Cordoba'
    | 'America/Argentina/Jujuy'
    | 'America/Argentina/La_Rioja'
    | 'America/Argentina/Mendoza'
    | 'America/Argentina/Rio_Gallegos'
    | 'America/Argentina/Salta'
    | 'America/Argentina/San_Juan'
    | 'America/Argentina/San_Luis'
    | 'America/Argentina/Tucuman'
    | 'America/Argentina/Ushuaia'
    | 'America/Aruba'
    | 'America/Asuncion'
    | 'America/Atikokan'
    | 'America/Atka'
    | 'America/Bahia'
    | 'America/Bahia_Banderas'
    | 'America/Barbados'
    | 'America/Belem'
    | 'America/Belize'
    | 'America/Blanc-Sablon'
    | 'America/Boa_Vista'
    | 'America/Bogota'
    | 'America/Boise'
    | 'America/Buenos_Aires'
    | 'America/Cambridge_Bay'
    | 'America/Campo_Grande'
    | 'America/Cancun'
    | 'America/Caracas'
    | 'America/Catamarca'
    | 'America/Cayenne'
    | 'America/Cayman'
    | 'America/Chicago'
    | 'America/Chihuahua'
    | 'America/Ciudad_Juarez'
    | 'America/Coral_Harbour'
    | 'America/Cordoba'
    | 'America/Costa_Rica'
    | 'America/Coyhaique'
    | 'America/Creston'
    | 'America/Cuiaba'
    | 'America/Curacao'
    | 'America/Danmarkshavn'
    | 'America/Dawson'
    | 'America/Dawson_Creek'
    | 'America/Denver'
    | 'America/Detroit'
    | 'America/Dominica'
    | 'America/Edmonton'
    | 'America/Eirunepe'
    | 'America/El_Salvador'
    | 'America/Ensenada'
    | 'America/Fort_Nelson'
    | 'America/Fort_Wayne'
    | 'America/Fortaleza'
    | 'America/Glace_Bay'
    | 'America/Godthab'
    | 'America/Goose_Bay'
    | 'America/Grand_Turk'
    | 'America/Grenada'
    | 'America/Guadeloupe'
    | 'America/Guatemala'
    | 'America/Guayaquil'
    | 'America/Guyana'
    | 'America/Halifax'
    | 'America/Havana'
    | 'America/Hermosillo'
    | 'America/Indiana/Indianapolis'
    | 'America/Indiana/Knox'
    | 'America/Indiana/Marengo'
    | 'America/Indiana/Petersburg'
    | 'America/Indiana/Tell_City'
    | 'America/Indiana/Vevay'
    | 'America/Indiana/Vincennes'
    | 'America/Indiana/Winamac'
    | 'America/Indianapolis'
    | 'America/Inuvik'
    | 'America/Iqaluit'
    | 'America/Jamaica'
    | 'America/Jujuy'
    | 'America/Juneau'
    | 'America/Kentucky/Louisville'
    | 'America/Kentucky/Monticello'
    | 'America/Knox_IN'
    | 'America/Kralendijk'
    | 'America/La_Paz'
    | 'America/Lima'
    | 'America/Los_Angeles'
    | 'America/Louisville'
    | 'America/Lower_Princes'
    | 'America/Maceio'
    | 'America/Managua'
    | 'America/Manaus'
    | 'America/Marigot'
    | 'America/Martinique'
    | 'America/Matamoros'
    | 'America/Mazatlan'
    | 'America/Mendoza'
    | 'America/Menominee'
    | 'America/Merida'
    | 'America/Metlakatla'
    | 'America/Mexico_City'
    | 'America/Miquelon'
    | 'America/Moncton'
    | 'America/Monterrey'
    | 'America/Montevideo'
    | 'America/Montreal'
    | 'America/Montserrat'
    | 'America/Nassau'
    | 'America/New_York'
    | 'America/Nipigon'
    | 'America/Nome'
    | 'America/Noronha'
    | 'America/North_Dakota/Beulah'
    | 'America/North_Dakota/Center'
    | 'America/North_Dakota/New_Salem'
    | 'America/Nuuk'
    | 'America/Ojinaga'
    | 'America/Panama'
    | 'America/Pangnirtung'
    | 'America/Paramaribo'
    | 'America/Phoenix'
    | 'America/Port-au-Prince'
    | 'America/Port_of_Spain'
    | 'America/Porto_Acre'
    | 'America/Porto_Velho'
    | 'America/Puerto_Rico'
    | 'America/Punta_Arenas'
    | 'America/Rainy_River'
    | 'America/Rankin_Inlet'
    | 'America/Recife'
    | 'America/Regina'
    | 'America/Resolute'
    | 'America/Rio_Branco'
    | 'America/Rosario'
    | 'America/Santa_Isabel'
    | 'America/Santarem'
    | 'America/Santiago'
    | 'America/Santo_Domingo'
    | 'America/Sao_Paulo'
    | 'America/Scoresbysund'
    | 'America/Shiprock'
    | 'America/Sitka'
    | 'America/St_Barthelemy'
    | 'America/St_Johns'
    | 'America/St_Kitts'
    | 'America/St_Lucia'
    | 'America/St_Thomas'
    | 'America/St_Vincent'
    | 'America/Swift_Current'
    | 'America/Tegucigalpa'
    | 'America/Thule'
    | 'America/Thunder_Bay'
    | 'America/Tijuana'
    | 'America/Toronto'
    | 'America/Tortola'
    | 'America/Vancouver'
    | 'America/Virgin'
    | 'America/Whitehorse'
    | 'America/Winnipeg'
    | 'America/Yakutat'
    | 'America/Yellowknife'
    | 'Antarctica/Casey'
    | 'Antarctica/Davis'
    | 'Antarctica/DumontDUrville'
    | 'Antarctica/Macquarie'
    | 'Antarctica/Mawson'
    | 'Antarctica/McMurdo'
    | 'Antarctica/Palmer'
    | 'Antarctica/Rothera'
    | 'Antarctica/South_Pole'
    | 'Antarctica/Syowa'
    | 'Antarctica/Troll'
    | 'Antarctica/Vostok'
    | 'Arctic/Longyearbyen'
    | 'Asia/Aden'
    | 'Asia/Almaty'
    | 'Asia/Amman'
    | 'Asia/Anadyr'
    | 'Asia/Aqtau'
    | 'Asia/Aqtobe'
    | 'Asia/Ashgabat'
    | 'Asia/Ashkhabad'
    | 'Asia/Atyrau'
    | 'Asia/Baghdad'
    | 'Asia/Bahrain'
    | 'Asia/Baku'
    | 'Asia/Bangkok'
    | 'Asia/Barnaul'
    | 'Asia/Beirut'
    | 'Asia/Bishkek'
    | 'Asia/Brunei'
    | 'Asia/Calcutta'
    | 'Asia/Chita'
    | 'Asia/Choibalsan'
    | 'Asia/Chongqing'
    | 'Asia/Chungking'
    | 'Asia/Colombo'
    | 'Asia/Dacca'
    | 'Asia/Damascus'
    | 'Asia/Dhaka'
    | 'Asia/Dili'
    | 'Asia/Dubai'
    | 'Asia/Dushanbe'
    | 'Asia/Famagusta'
    | 'Asia/Gaza'
    | 'Asia/Harbin'
    | 'Asia/Hebron'
    | 'Asia/Ho_Chi_Minh'
    | 'Asia/Hong_Kong'
    | 'Asia/Hovd'
    | 'Asia/Irkutsk'
    | 'Asia/Istanbul'
    | 'Asia/Jakarta'
    | 'Asia/Jayapura'
    | 'Asia/Jerusalem'
    | 'Asia/Kabul'
    | 'Asia/Kamchatka'
    | 'Asia/Karachi'
    | 'Asia/Kashgar'
    | 'Asia/Kathmandu'
    | 'Asia/Katmandu'
    | 'Asia/Khandyga'
    | 'Asia/Kolkata'
    | 'Asia/Krasnoyarsk'
    | 'Asia/Kuala_Lumpur'
    | 'Asia/Kuching'
    | 'Asia/Kuwait'
    | 'Asia/Macao'
    | 'Asia/Macau'
    | 'Asia/Magadan'
    | 'Asia/Makassar'
    | 'Asia/Manila'
    | 'Asia/Muscat'
    | 'Asia/Nicosia'
    | 'Asia/Novokuznetsk'
    | 'Asia/Novosibirsk'
    | 'Asia/Omsk'
    | 'Asia/Oral'
    | 'Asia/Phnom_Penh'
    | 'Asia/Pontianak'
    | 'Asia/Pyongyang'
    | 'Asia/Qatar'
    | 'Asia/Qostanay'
    | 'Asia/Qyzylorda'
    | 'Asia/Rangoon'
    | 'Asia/Riyadh'
    | 'Asia/Saigon'
    | 'Asia/Sakhalin'
    | 'Asia/Samarkand'
    | 'Asia/Seoul'
    | 'Asia/Shanghai'
    | 'Asia/Singapore'
    | 'Asia/Srednekolymsk'
    | 'Asia/Taipei'
    | 'Asia/Tashkent'
    | 'Asia/Tbilisi'
    | 'Asia/Tehran'
    | 'Asia/Tel_Aviv'
    | 'Asia/Thimbu'
    | 'Asia/Thimphu'
    | 'Asia/Tokyo'
    | 'Asia/Tomsk'
    | 'Asia/Ujung_Pandang'
    | 'Asia/Ulaanbaatar'
    | 'Asia/Ulan_Bator'
    | 'Asia/Urumqi'
    | 'Asia/Ust-Nera'
    | 'Asia/Vientiane'
    | 'Asia/Vladivostok'
    | 'Asia/Yakutsk'
    | 'Asia/Yangon'
    | 'Asia/Yekaterinburg'
    | 'Asia/Yerevan'
    | 'Atlantic/Azores'
    | 'Atlantic/Bermuda'
    | 'Atlantic/Canary'
    | 'Atlantic/Cape_Verde'
    | 'Atlantic/Faeroe'
    | 'Atlantic/Faroe'
    | 'Atlantic/Jan_Mayen'
    | 'Atlantic/Madeira'
    | 'Atlantic/Reykjavik'
    | 'Atlantic/South_Georgia'
    | 'Atlantic/St_Helena'
    | 'Atlantic/Stanley'
    | 'Australia/ACT'
    | 'Australia/Adelaide'
    | 'Australia/Brisbane'
    | 'Australia/Broken_Hill'
    | 'Australia/Canberra'
    | 'Australia/Currie'
    | 'Australia/Darwin'
    | 'Australia/Eucla'
    | 'Australia/Hobart'
    | 'Australia/LHI'
    | 'Australia/Lindeman'
    | 'Australia/Lord_Howe'
    | 'Australia/Melbourne'
    | 'Australia/NSW'
    | 'Australia/North'
    | 'Australia/Perth'
    | 'Australia/Queensland'
    | 'Australia/South'
    | 'Australia/Sydney'
    | 'Australia/Tasmania'
    | 'Australia/Victoria'
    | 'Australia/West'
    | 'Australia/Yancowinna'
    | 'Brazil/Acre'
    | 'Brazil/DeNoronha'
    | 'Brazil/East'
    | 'Brazil/West'
    | 'CET'
    | 'CST6CDT'
    | 'Canada/Atlantic'
    | 'Canada/Central'
    | 'Canada/Eastern'
    | 'Canada/Mountain'
    | 'Canada/Newfoundland'
    | 'Canada/Pacific'
    | 'Canada/Saskatchewan'
    | 'Canada/Yukon'
    | 'Chile/Continental'
    | 'Chile/EasterIsland'
    | 'Cuba'
    | 'EET'
    | 'EST'
    | 'EST5EDT'
    | 'Egypt'
    | 'Eire'
    | 'Etc/GMT'
    | 'Etc/GMT+0'
    | 'Etc/GMT+1'
    | 'Etc/GMT+10'
    | 'Etc/GMT+11'
    | 'Etc/GMT+12'
    | 'Etc/GMT+2'
    | 'Etc/GMT+3'
    | 'Etc/GMT+4'
    | 'Etc/GMT+5'
    | 'Etc/GMT+6'
    | 'Etc/GMT+7'
    | 'Etc/GMT+8'
    | 'Etc/GMT+9'
    | 'Etc/GMT-0'
    | 'Etc/GMT-1'
    | 'Etc/GMT-10'
    | 'Etc/GMT-11'
    | 'Etc/GMT-12'
    | 'Etc/GMT-13'
    | 'Etc/GMT-14'
    | 'Etc/GMT-2'
    | 'Etc/GMT-3'
    | 'Etc/GMT-4'
    | 'Etc/GMT-5'
    | 'Etc/GMT-6'
    | 'Etc/GMT-7'
    | 'Etc/GMT-8'
    | 'Etc/GMT-9'
    | 'Etc/GMT0'
    | 'Etc/Greenwich'
    | 'Etc/UCT'
    | 'Etc/UTC'
    | 'Etc/Universal'
    | 'Etc/Zulu'
    | 'Europe/Amsterdam'
    | 'Europe/Andorra'
    | 'Europe/Astrakhan'
    | 'Europe/Athens'
    | 'Europe/Belfast'
    | 'Europe/Belgrade'
    | 'Europe/Berlin'
    | 'Europe/Bratislava'
    | 'Europe/Brussels'
    | 'Europe/Bucharest'
    | 'Europe/Budapest'
    | 'Europe/Busingen'
    | 'Europe/Chisinau'
    | 'Europe/Copenhagen'
    | 'Europe/Dublin'
    | 'Europe/Gibraltar'
    | 'Europe/Guernsey'
    | 'Europe/Helsinki'
    | 'Europe/Isle_of_Man'
    | 'Europe/Istanbul'
    | 'Europe/Jersey'
    | 'Europe/Kaliningrad'
    | 'Europe/Kiev'
    | 'Europe/Kirov'
    | 'Europe/Kyiv'
    | 'Europe/Lisbon'
    | 'Europe/Ljubljana'
    | 'Europe/London'
    | 'Europe/Luxembourg'
    | 'Europe/Madrid'
    | 'Europe/Malta'
    | 'Europe/Mariehamn'
    | 'Europe/Minsk'
    | 'Europe/Monaco'
    | 'Europe/Moscow'
    | 'Europe/Nicosia'
    | 'Europe/Oslo'
    | 'Europe/Paris'
    | 'Europe/Podgorica'
    | 'Europe/Prague'
    | 'Europe/Riga'
    | 'Europe/Rome'
    | 'Europe/Samara'
    | 'Europe/San_Marino'
    | 'Europe/Sarajevo'
    | 'Europe/Saratov'
    | 'Europe/Simferopol'
    | 'Europe/Skopje'
    | 'Europe/Sofia'
    | 'Europe/Stockholm'
    | 'Europe/Tallinn'
    | 'Europe/Tirane'
    | 'Europe/Tiraspol'
    | 'Europe/Ulyanovsk'
    | 'Europe/Uzhgorod'
    | 'Europe/Vaduz'
    | 'Europe/Vatican'
    | 'Europe/Vienna'
    | 'Europe/Vilnius'
    | 'Europe/Volgograd'
    | 'Europe/Warsaw'
    | 'Europe/Zagreb'
    | 'Europe/Zaporozhye'
    | 'Europe/Zurich'
    | 'Factory'
    | 'GB'
    | 'GB-Eire'
    | 'GMT'
    | 'GMT+0'
    | 'GMT-0'
    | 'GMT0'
    | 'Greenwich'
    | 'HST'
    | 'Hongkong'
    | 'Iceland'
    | 'Indian/Antananarivo'
    | 'Indian/Chagos'
    | 'Indian/Christmas'
    | 'Indian/Cocos'
    | 'Indian/Comoro'
    | 'Indian/Kerguelen'
    | 'Indian/Mahe'
    | 'Indian/Maldives'
    | 'Indian/Mauritius'
    | 'Indian/Mayotte'
    | 'Indian/Reunion'
    | 'Iran'
    | 'Israel'
    | 'Jamaica'
    | 'Japan'
    | 'Kwajalein'
    | 'Libya'
    | 'MET'
    | 'MST'
    | 'MST7MDT'
    | 'Mexico/BajaNorte'
    | 'Mexico/BajaSur'
    | 'Mexico/General'
    | 'NZ'
    | 'NZ-CHAT'
    | 'Navajo'
    | 'PRC'
    | 'PST8PDT'
    | 'Pacific/Apia'
    | 'Pacific/Auckland'
    | 'Pacific/Bougainville'
    | 'Pacific/Chatham'
    | 'Pacific/Chuuk'
    | 'Pacific/Easter'
    | 'Pacific/Efate'
    | 'Pacific/Enderbury'
    | 'Pacific/Fakaofo'
    | 'Pacific/Fiji'
    | 'Pacific/Funafuti'
    | 'Pacific/Galapagos'
    | 'Pacific/Gambier'
    | 'Pacific/Guadalcanal'
    | 'Pacific/Guam'
    | 'Pacific/Honolulu'
    | 'Pacific/Johnston'
    | 'Pacific/Kanton'
    | 'Pacific/Kiritimati'
    | 'Pacific/Kosrae'
    | 'Pacific/Kwajalein'
    | 'Pacific/Majuro'
    | 'Pacific/Marquesas'
    | 'Pacific/Midway'
    | 'Pacific/Nauru'
    | 'Pacific/Niue'
    | 'Pacific/Norfolk'
    | 'Pacific/Noumea'
    | 'Pacific/Pago_Pago'
    | 'Pacific/Palau'
    | 'Pacific/Pitcairn'
    | 'Pacific/Pohnpei'
    | 'Pacific/Ponape'
    | 'Pacific/Port_Moresby'
    | 'Pacific/Rarotonga'
    | 'Pacific/Saipan'
    | 'Pacific/Samoa'
    | 'Pacific/Tahiti'
    | 'Pacific/Tarawa'
    | 'Pacific/Tongatapu'
    | 'Pacific/Truk'
    | 'Pacific/Wake'
    | 'Pacific/Wallis'
    | 'Pacific/Yap'
    | 'Poland'
    | 'Portugal'
    | 'ROC'
    | 'ROK'
    | 'Singapore'
    | 'Turkey'
    | 'UCT'
    | 'US/Alaska'
    | 'US/Aleutian'
    | 'US/Arizona'
    | 'US/Central'
    | 'US/East-Indiana'
    | 'US/Eastern'
    | 'US/Hawaii'
    | 'US/Indiana-Starke'
    | 'US/Michigan'
    | 'US/Mountain'
    | 'US/Pacific'
    | 'US/Samoa'
    | 'UTC'
    | 'Universal'
    | 'W-SU'
    | 'WET'
    | 'Zulu'
    | null
  /**
   * Frequency used to derive claimable occurrences.
   */
  frequency?: BonusCampaignFrequency | null
  /**
   * Token in which the reward is denominated.
   */
  tokenType?: CryptoTokenType | null
  /**
   * Reward committed for each successful earning, in natural units.
   */
  rewardAmount?: number | string | null
  /**
   * Maximum reward amount per occurrence, in natural units.
   */
  distributionLimit?: number | string | null
}

export type UpdateCampaignRequest = {
  title?: string
  description?: string
  howToUse?: string | null
  logoUrl?: string | null
  claimSteps?: Array<string>
  redirectUrl?: string | null
  redirectButtonLabel?: string | null
  pointsCost?: number
  maxClaimsPerPeriod?: number
  claimPeriod?: ClaimPeriod
  validFrom?: string | null
  validTo?: string | null
}

/**
 * Request schema for updating an offer's price.
 */
export type UpdateOfferPriceRequest = {
  price: number
}

export type UpdateTargetingRequest = {
  slugName: string
  priority?: number | null
}

/**
 * Request schema for updating a token offer. token_amount is not updatable.
 */
export type UpdateTokenOfferRequest = {
  startDate?: string | null
  endDate?: string | null
  price?: number | null
  countryCodes?: Array<string> | null
  clientTypes?: Array<ClientType> | null
  purchaseLimitWeekly?: number | null
  purchaseLimitMonthly?: number | null
  purchaseLimitTotal?: number | null
  purchaseLimitAppInstanceWeekly?: number | null
  targetingId?: number | null
}

export type UpdateTrafficCampaignRequest = {
  slug?: string | null
  targetUrl?: string | null
  countryCodes?: Array<string> | null
  clientTypes?: Array<ClientType> | null
  validFrom?: string | null
  validTo?: string | null
  rewardAmount?: number | null
  periodicity?: TrafficPeriodicity | null
  featureFlag?: FeatureFlag | null
  minimalVersion?: string | null
  maximalVersion?: string | null
}

export type UsdtMarketInfoSchema = {
  isGlobalLimitReached: boolean
}

/**
 * Single USDT offer item.
 */
export type UsdtOfferItem = {
  /**
   * Unique identifier of the USDT offer
   */
  id: number
  price: Amount
  state: MarketItemState
  offerHash: string
  extra: UsdtOfferItemExtra
}

export type UsdtOfferItemExtra = {
  /**
   * Amount of USDT in this offer
   */
  usdtAmount: string
}

/**
 * Response containing list of USDT offers.
 */
export type UsdtOffersResponse = {
  items: Array<UsdtOfferItem>
}

export type UsdtPurchaseHistorySchema = {
  id: number
  offerId: number
  usdtAmount: string
  walletAddress: string
  phoneNumber: string
  createdAt: string
}

/**
 * Request to purchase USDT exchange.
 */
export type UsdtPurchaseRequest = {
  /**
   * Item ID
   */
  id: number
  offerHash: string
  /**
   * Phone number for USDT transfer
   */
  phoneNumber: string
}

/**
 * Response for USDT exchange purchase.
 */
export type UsdtPurchaseResponse = {
  /**
   * ID of the PointsLog entry used to purchase this USDT exchange. It can be used to link the generated cashlink with points log data in points history view.
   */
  relatedPointsLogId: number
}

export type UsdtWalletRateLimitStatus = {
  walletAddress: string | null
  minuteUsed: number
  minuteLimit: number
  dailyUsed: number
  dailyLimit: number
}

export type UserClientSchema = {
  /**
   * Device identifier.
   */
  haid: string
  /**
   * Opera User ID
   */
  huid?: string | null
  clientType: ClientType
  clientVersion: string
  /**
   * Version of the embedded client SDK. Defaults to empty value for backward compatibility.
   */
  sdkVersion?: string | null
  /**
   * Client-supplied country code. Any string is accepted; if it is a valid ISO 3166-1 alpha-2 country code it is used as-is, otherwise the country code detected by GeoIP is used as a fallback.
   */
  userCountry: string
  /**
   * Representing client installation time.
   */
  installationTime?: string | null
  /**
   * Device timezone. If not provided, the country code is used to determine timezone.
   */
  timezone?:
    | 'Africa/Abidjan'
    | 'Africa/Accra'
    | 'Africa/Addis_Ababa'
    | 'Africa/Algiers'
    | 'Africa/Asmara'
    | 'Africa/Asmera'
    | 'Africa/Bamako'
    | 'Africa/Bangui'
    | 'Africa/Banjul'
    | 'Africa/Bissau'
    | 'Africa/Blantyre'
    | 'Africa/Brazzaville'
    | 'Africa/Bujumbura'
    | 'Africa/Cairo'
    | 'Africa/Casablanca'
    | 'Africa/Ceuta'
    | 'Africa/Conakry'
    | 'Africa/Dakar'
    | 'Africa/Dar_es_Salaam'
    | 'Africa/Djibouti'
    | 'Africa/Douala'
    | 'Africa/El_Aaiun'
    | 'Africa/Freetown'
    | 'Africa/Gaborone'
    | 'Africa/Harare'
    | 'Africa/Johannesburg'
    | 'Africa/Juba'
    | 'Africa/Kampala'
    | 'Africa/Khartoum'
    | 'Africa/Kigali'
    | 'Africa/Kinshasa'
    | 'Africa/Lagos'
    | 'Africa/Libreville'
    | 'Africa/Lome'
    | 'Africa/Luanda'
    | 'Africa/Lubumbashi'
    | 'Africa/Lusaka'
    | 'Africa/Malabo'
    | 'Africa/Maputo'
    | 'Africa/Maseru'
    | 'Africa/Mbabane'
    | 'Africa/Mogadishu'
    | 'Africa/Monrovia'
    | 'Africa/Nairobi'
    | 'Africa/Ndjamena'
    | 'Africa/Niamey'
    | 'Africa/Nouakchott'
    | 'Africa/Ouagadougou'
    | 'Africa/Porto-Novo'
    | 'Africa/Sao_Tome'
    | 'Africa/Timbuktu'
    | 'Africa/Tripoli'
    | 'Africa/Tunis'
    | 'Africa/Windhoek'
    | 'America/Adak'
    | 'America/Anchorage'
    | 'America/Anguilla'
    | 'America/Antigua'
    | 'America/Araguaina'
    | 'America/Argentina/Buenos_Aires'
    | 'America/Argentina/Catamarca'
    | 'America/Argentina/ComodRivadavia'
    | 'America/Argentina/Cordoba'
    | 'America/Argentina/Jujuy'
    | 'America/Argentina/La_Rioja'
    | 'America/Argentina/Mendoza'
    | 'America/Argentina/Rio_Gallegos'
    | 'America/Argentina/Salta'
    | 'America/Argentina/San_Juan'
    | 'America/Argentina/San_Luis'
    | 'America/Argentina/Tucuman'
    | 'America/Argentina/Ushuaia'
    | 'America/Aruba'
    | 'America/Asuncion'
    | 'America/Atikokan'
    | 'America/Atka'
    | 'America/Bahia'
    | 'America/Bahia_Banderas'
    | 'America/Barbados'
    | 'America/Belem'
    | 'America/Belize'
    | 'America/Blanc-Sablon'
    | 'America/Boa_Vista'
    | 'America/Bogota'
    | 'America/Boise'
    | 'America/Buenos_Aires'
    | 'America/Cambridge_Bay'
    | 'America/Campo_Grande'
    | 'America/Cancun'
    | 'America/Caracas'
    | 'America/Catamarca'
    | 'America/Cayenne'
    | 'America/Cayman'
    | 'America/Chicago'
    | 'America/Chihuahua'
    | 'America/Ciudad_Juarez'
    | 'America/Coral_Harbour'
    | 'America/Cordoba'
    | 'America/Costa_Rica'
    | 'America/Coyhaique'
    | 'America/Creston'
    | 'America/Cuiaba'
    | 'America/Curacao'
    | 'America/Danmarkshavn'
    | 'America/Dawson'
    | 'America/Dawson_Creek'
    | 'America/Denver'
    | 'America/Detroit'
    | 'America/Dominica'
    | 'America/Edmonton'
    | 'America/Eirunepe'
    | 'America/El_Salvador'
    | 'America/Ensenada'
    | 'America/Fort_Nelson'
    | 'America/Fort_Wayne'
    | 'America/Fortaleza'
    | 'America/Glace_Bay'
    | 'America/Godthab'
    | 'America/Goose_Bay'
    | 'America/Grand_Turk'
    | 'America/Grenada'
    | 'America/Guadeloupe'
    | 'America/Guatemala'
    | 'America/Guayaquil'
    | 'America/Guyana'
    | 'America/Halifax'
    | 'America/Havana'
    | 'America/Hermosillo'
    | 'America/Indiana/Indianapolis'
    | 'America/Indiana/Knox'
    | 'America/Indiana/Marengo'
    | 'America/Indiana/Petersburg'
    | 'America/Indiana/Tell_City'
    | 'America/Indiana/Vevay'
    | 'America/Indiana/Vincennes'
    | 'America/Indiana/Winamac'
    | 'America/Indianapolis'
    | 'America/Inuvik'
    | 'America/Iqaluit'
    | 'America/Jamaica'
    | 'America/Jujuy'
    | 'America/Juneau'
    | 'America/Kentucky/Louisville'
    | 'America/Kentucky/Monticello'
    | 'America/Knox_IN'
    | 'America/Kralendijk'
    | 'America/La_Paz'
    | 'America/Lima'
    | 'America/Los_Angeles'
    | 'America/Louisville'
    | 'America/Lower_Princes'
    | 'America/Maceio'
    | 'America/Managua'
    | 'America/Manaus'
    | 'America/Marigot'
    | 'America/Martinique'
    | 'America/Matamoros'
    | 'America/Mazatlan'
    | 'America/Mendoza'
    | 'America/Menominee'
    | 'America/Merida'
    | 'America/Metlakatla'
    | 'America/Mexico_City'
    | 'America/Miquelon'
    | 'America/Moncton'
    | 'America/Monterrey'
    | 'America/Montevideo'
    | 'America/Montreal'
    | 'America/Montserrat'
    | 'America/Nassau'
    | 'America/New_York'
    | 'America/Nipigon'
    | 'America/Nome'
    | 'America/Noronha'
    | 'America/North_Dakota/Beulah'
    | 'America/North_Dakota/Center'
    | 'America/North_Dakota/New_Salem'
    | 'America/Nuuk'
    | 'America/Ojinaga'
    | 'America/Panama'
    | 'America/Pangnirtung'
    | 'America/Paramaribo'
    | 'America/Phoenix'
    | 'America/Port-au-Prince'
    | 'America/Port_of_Spain'
    | 'America/Porto_Acre'
    | 'America/Porto_Velho'
    | 'America/Puerto_Rico'
    | 'America/Punta_Arenas'
    | 'America/Rainy_River'
    | 'America/Rankin_Inlet'
    | 'America/Recife'
    | 'America/Regina'
    | 'America/Resolute'
    | 'America/Rio_Branco'
    | 'America/Rosario'
    | 'America/Santa_Isabel'
    | 'America/Santarem'
    | 'America/Santiago'
    | 'America/Santo_Domingo'
    | 'America/Sao_Paulo'
    | 'America/Scoresbysund'
    | 'America/Shiprock'
    | 'America/Sitka'
    | 'America/St_Barthelemy'
    | 'America/St_Johns'
    | 'America/St_Kitts'
    | 'America/St_Lucia'
    | 'America/St_Thomas'
    | 'America/St_Vincent'
    | 'America/Swift_Current'
    | 'America/Tegucigalpa'
    | 'America/Thule'
    | 'America/Thunder_Bay'
    | 'America/Tijuana'
    | 'America/Toronto'
    | 'America/Tortola'
    | 'America/Vancouver'
    | 'America/Virgin'
    | 'America/Whitehorse'
    | 'America/Winnipeg'
    | 'America/Yakutat'
    | 'America/Yellowknife'
    | 'Antarctica/Casey'
    | 'Antarctica/Davis'
    | 'Antarctica/DumontDUrville'
    | 'Antarctica/Macquarie'
    | 'Antarctica/Mawson'
    | 'Antarctica/McMurdo'
    | 'Antarctica/Palmer'
    | 'Antarctica/Rothera'
    | 'Antarctica/South_Pole'
    | 'Antarctica/Syowa'
    | 'Antarctica/Troll'
    | 'Antarctica/Vostok'
    | 'Arctic/Longyearbyen'
    | 'Asia/Aden'
    | 'Asia/Almaty'
    | 'Asia/Amman'
    | 'Asia/Anadyr'
    | 'Asia/Aqtau'
    | 'Asia/Aqtobe'
    | 'Asia/Ashgabat'
    | 'Asia/Ashkhabad'
    | 'Asia/Atyrau'
    | 'Asia/Baghdad'
    | 'Asia/Bahrain'
    | 'Asia/Baku'
    | 'Asia/Bangkok'
    | 'Asia/Barnaul'
    | 'Asia/Beirut'
    | 'Asia/Bishkek'
    | 'Asia/Brunei'
    | 'Asia/Calcutta'
    | 'Asia/Chita'
    | 'Asia/Choibalsan'
    | 'Asia/Chongqing'
    | 'Asia/Chungking'
    | 'Asia/Colombo'
    | 'Asia/Dacca'
    | 'Asia/Damascus'
    | 'Asia/Dhaka'
    | 'Asia/Dili'
    | 'Asia/Dubai'
    | 'Asia/Dushanbe'
    | 'Asia/Famagusta'
    | 'Asia/Gaza'
    | 'Asia/Harbin'
    | 'Asia/Hebron'
    | 'Asia/Ho_Chi_Minh'
    | 'Asia/Hong_Kong'
    | 'Asia/Hovd'
    | 'Asia/Irkutsk'
    | 'Asia/Istanbul'
    | 'Asia/Jakarta'
    | 'Asia/Jayapura'
    | 'Asia/Jerusalem'
    | 'Asia/Kabul'
    | 'Asia/Kamchatka'
    | 'Asia/Karachi'
    | 'Asia/Kashgar'
    | 'Asia/Kathmandu'
    | 'Asia/Katmandu'
    | 'Asia/Khandyga'
    | 'Asia/Kolkata'
    | 'Asia/Krasnoyarsk'
    | 'Asia/Kuala_Lumpur'
    | 'Asia/Kuching'
    | 'Asia/Kuwait'
    | 'Asia/Macao'
    | 'Asia/Macau'
    | 'Asia/Magadan'
    | 'Asia/Makassar'
    | 'Asia/Manila'
    | 'Asia/Muscat'
    | 'Asia/Nicosia'
    | 'Asia/Novokuznetsk'
    | 'Asia/Novosibirsk'
    | 'Asia/Omsk'
    | 'Asia/Oral'
    | 'Asia/Phnom_Penh'
    | 'Asia/Pontianak'
    | 'Asia/Pyongyang'
    | 'Asia/Qatar'
    | 'Asia/Qostanay'
    | 'Asia/Qyzylorda'
    | 'Asia/Rangoon'
    | 'Asia/Riyadh'
    | 'Asia/Saigon'
    | 'Asia/Sakhalin'
    | 'Asia/Samarkand'
    | 'Asia/Seoul'
    | 'Asia/Shanghai'
    | 'Asia/Singapore'
    | 'Asia/Srednekolymsk'
    | 'Asia/Taipei'
    | 'Asia/Tashkent'
    | 'Asia/Tbilisi'
    | 'Asia/Tehran'
    | 'Asia/Tel_Aviv'
    | 'Asia/Thimbu'
    | 'Asia/Thimphu'
    | 'Asia/Tokyo'
    | 'Asia/Tomsk'
    | 'Asia/Ujung_Pandang'
    | 'Asia/Ulaanbaatar'
    | 'Asia/Ulan_Bator'
    | 'Asia/Urumqi'
    | 'Asia/Ust-Nera'
    | 'Asia/Vientiane'
    | 'Asia/Vladivostok'
    | 'Asia/Yakutsk'
    | 'Asia/Yangon'
    | 'Asia/Yekaterinburg'
    | 'Asia/Yerevan'
    | 'Atlantic/Azores'
    | 'Atlantic/Bermuda'
    | 'Atlantic/Canary'
    | 'Atlantic/Cape_Verde'
    | 'Atlantic/Faeroe'
    | 'Atlantic/Faroe'
    | 'Atlantic/Jan_Mayen'
    | 'Atlantic/Madeira'
    | 'Atlantic/Reykjavik'
    | 'Atlantic/South_Georgia'
    | 'Atlantic/St_Helena'
    | 'Atlantic/Stanley'
    | 'Australia/ACT'
    | 'Australia/Adelaide'
    | 'Australia/Brisbane'
    | 'Australia/Broken_Hill'
    | 'Australia/Canberra'
    | 'Australia/Currie'
    | 'Australia/Darwin'
    | 'Australia/Eucla'
    | 'Australia/Hobart'
    | 'Australia/LHI'
    | 'Australia/Lindeman'
    | 'Australia/Lord_Howe'
    | 'Australia/Melbourne'
    | 'Australia/NSW'
    | 'Australia/North'
    | 'Australia/Perth'
    | 'Australia/Queensland'
    | 'Australia/South'
    | 'Australia/Sydney'
    | 'Australia/Tasmania'
    | 'Australia/Victoria'
    | 'Australia/West'
    | 'Australia/Yancowinna'
    | 'Brazil/Acre'
    | 'Brazil/DeNoronha'
    | 'Brazil/East'
    | 'Brazil/West'
    | 'CET'
    | 'CST6CDT'
    | 'Canada/Atlantic'
    | 'Canada/Central'
    | 'Canada/Eastern'
    | 'Canada/Mountain'
    | 'Canada/Newfoundland'
    | 'Canada/Pacific'
    | 'Canada/Saskatchewan'
    | 'Canada/Yukon'
    | 'Chile/Continental'
    | 'Chile/EasterIsland'
    | 'Cuba'
    | 'EET'
    | 'EST'
    | 'EST5EDT'
    | 'Egypt'
    | 'Eire'
    | 'Etc/GMT'
    | 'Etc/GMT+0'
    | 'Etc/GMT+1'
    | 'Etc/GMT+10'
    | 'Etc/GMT+11'
    | 'Etc/GMT+12'
    | 'Etc/GMT+2'
    | 'Etc/GMT+3'
    | 'Etc/GMT+4'
    | 'Etc/GMT+5'
    | 'Etc/GMT+6'
    | 'Etc/GMT+7'
    | 'Etc/GMT+8'
    | 'Etc/GMT+9'
    | 'Etc/GMT-0'
    | 'Etc/GMT-1'
    | 'Etc/GMT-10'
    | 'Etc/GMT-11'
    | 'Etc/GMT-12'
    | 'Etc/GMT-13'
    | 'Etc/GMT-14'
    | 'Etc/GMT-2'
    | 'Etc/GMT-3'
    | 'Etc/GMT-4'
    | 'Etc/GMT-5'
    | 'Etc/GMT-6'
    | 'Etc/GMT-7'
    | 'Etc/GMT-8'
    | 'Etc/GMT-9'
    | 'Etc/GMT0'
    | 'Etc/Greenwich'
    | 'Etc/UCT'
    | 'Etc/UTC'
    | 'Etc/Universal'
    | 'Etc/Zulu'
    | 'Europe/Amsterdam'
    | 'Europe/Andorra'
    | 'Europe/Astrakhan'
    | 'Europe/Athens'
    | 'Europe/Belfast'
    | 'Europe/Belgrade'
    | 'Europe/Berlin'
    | 'Europe/Bratislava'
    | 'Europe/Brussels'
    | 'Europe/Bucharest'
    | 'Europe/Budapest'
    | 'Europe/Busingen'
    | 'Europe/Chisinau'
    | 'Europe/Copenhagen'
    | 'Europe/Dublin'
    | 'Europe/Gibraltar'
    | 'Europe/Guernsey'
    | 'Europe/Helsinki'
    | 'Europe/Isle_of_Man'
    | 'Europe/Istanbul'
    | 'Europe/Jersey'
    | 'Europe/Kaliningrad'
    | 'Europe/Kiev'
    | 'Europe/Kirov'
    | 'Europe/Kyiv'
    | 'Europe/Lisbon'
    | 'Europe/Ljubljana'
    | 'Europe/London'
    | 'Europe/Luxembourg'
    | 'Europe/Madrid'
    | 'Europe/Malta'
    | 'Europe/Mariehamn'
    | 'Europe/Minsk'
    | 'Europe/Monaco'
    | 'Europe/Moscow'
    | 'Europe/Nicosia'
    | 'Europe/Oslo'
    | 'Europe/Paris'
    | 'Europe/Podgorica'
    | 'Europe/Prague'
    | 'Europe/Riga'
    | 'Europe/Rome'
    | 'Europe/Samara'
    | 'Europe/San_Marino'
    | 'Europe/Sarajevo'
    | 'Europe/Saratov'
    | 'Europe/Simferopol'
    | 'Europe/Skopje'
    | 'Europe/Sofia'
    | 'Europe/Stockholm'
    | 'Europe/Tallinn'
    | 'Europe/Tirane'
    | 'Europe/Tiraspol'
    | 'Europe/Ulyanovsk'
    | 'Europe/Uzhgorod'
    | 'Europe/Vaduz'
    | 'Europe/Vatican'
    | 'Europe/Vienna'
    | 'Europe/Vilnius'
    | 'Europe/Volgograd'
    | 'Europe/Warsaw'
    | 'Europe/Zagreb'
    | 'Europe/Zaporozhye'
    | 'Europe/Zurich'
    | 'Factory'
    | 'GB'
    | 'GB-Eire'
    | 'GMT'
    | 'GMT+0'
    | 'GMT-0'
    | 'GMT0'
    | 'Greenwich'
    | 'HST'
    | 'Hongkong'
    | 'Iceland'
    | 'Indian/Antananarivo'
    | 'Indian/Chagos'
    | 'Indian/Christmas'
    | 'Indian/Cocos'
    | 'Indian/Comoro'
    | 'Indian/Kerguelen'
    | 'Indian/Mahe'
    | 'Indian/Maldives'
    | 'Indian/Mauritius'
    | 'Indian/Mayotte'
    | 'Indian/Reunion'
    | 'Iran'
    | 'Israel'
    | 'Jamaica'
    | 'Japan'
    | 'Kwajalein'
    | 'Libya'
    | 'MET'
    | 'MST'
    | 'MST7MDT'
    | 'Mexico/BajaNorte'
    | 'Mexico/BajaSur'
    | 'Mexico/General'
    | 'NZ'
    | 'NZ-CHAT'
    | 'Navajo'
    | 'PRC'
    | 'PST8PDT'
    | 'Pacific/Apia'
    | 'Pacific/Auckland'
    | 'Pacific/Bougainville'
    | 'Pacific/Chatham'
    | 'Pacific/Chuuk'
    | 'Pacific/Easter'
    | 'Pacific/Efate'
    | 'Pacific/Enderbury'
    | 'Pacific/Fakaofo'
    | 'Pacific/Fiji'
    | 'Pacific/Funafuti'
    | 'Pacific/Galapagos'
    | 'Pacific/Gambier'
    | 'Pacific/Guadalcanal'
    | 'Pacific/Guam'
    | 'Pacific/Honolulu'
    | 'Pacific/Johnston'
    | 'Pacific/Kanton'
    | 'Pacific/Kiritimati'
    | 'Pacific/Kosrae'
    | 'Pacific/Kwajalein'
    | 'Pacific/Majuro'
    | 'Pacific/Marquesas'
    | 'Pacific/Midway'
    | 'Pacific/Nauru'
    | 'Pacific/Niue'
    | 'Pacific/Norfolk'
    | 'Pacific/Noumea'
    | 'Pacific/Pago_Pago'
    | 'Pacific/Palau'
    | 'Pacific/Pitcairn'
    | 'Pacific/Pohnpei'
    | 'Pacific/Ponape'
    | 'Pacific/Port_Moresby'
    | 'Pacific/Rarotonga'
    | 'Pacific/Saipan'
    | 'Pacific/Samoa'
    | 'Pacific/Tahiti'
    | 'Pacific/Tarawa'
    | 'Pacific/Tongatapu'
    | 'Pacific/Truk'
    | 'Pacific/Wake'
    | 'Pacific/Wallis'
    | 'Pacific/Yap'
    | 'Poland'
    | 'Portugal'
    | 'ROC'
    | 'ROK'
    | 'Singapore'
    | 'Turkey'
    | 'UCT'
    | 'US/Alaska'
    | 'US/Aleutian'
    | 'US/Arizona'
    | 'US/Central'
    | 'US/East-Indiana'
    | 'US/Eastern'
    | 'US/Hawaii'
    | 'US/Indiana-Starke'
    | 'US/Michigan'
    | 'US/Mountain'
    | 'US/Pacific'
    | 'US/Samoa'
    | 'UTC'
    | 'Universal'
    | 'W-SU'
    | 'WET'
    | 'Zulu'
    | null
  /**
   * When True, sets the App Instance to 'internal.' This only has an effect if `debug_features_enabled` is also True.
   */
  internal?: boolean
}

export type UserConfig = {
  /**
   * Action to trigger on FAQ click in footer. None indicates that FAQ action should not be shown.
   */
  faq: ActionNoText | null
  /**
   * Action to trigger on terms click in footer.
   */
  terms: ActionNoText
  walletRequired: boolean
  cashoutEnabled: boolean
  leaderboardEnabled: boolean
  devSettingsEnabled: boolean
  /**
   * User level for VIP Free Data status in Opera Mini. For details check design doc: https://docs.google.com/document/d/1VawYcDmiIzD_18TXkXoQpIGn6U0e026GvDmsv_z9E8U/edit?tab=t.dl198opmjuzj#heading=h.b72vucmb6ihk
   */
  userLevel: UserLevel
  /**
   * URL template for transaction explorer links. Expects the '{hash}' placeholder token for client-side string replacement.
   */
  transactionLinkTemplate?: string
}

export type UserEarningsTotal = {
  /**
   * 1 unit represents 1/10_000 cUSD
   */
  totalAmountGranted: number
  lastPayout: UserLastPayout | null
  /**
   * Pending loyalty bonus - amount that user can receive after MiniPay wallet activation. 1 unit represents 1/10_000 cUSD
   */
  pendingLoyaltyBonusAmount: number
}

export type UserFcmToken = {
  fcmToken: string
  language: string
}

export type UserFcmTokenSchema = {
  id: number
  appInstanceId: number | null
  value: string
  language: string
  createdAt: string
  updatedAt: string
}

export type UserFcmTokensResponse = {
  items?: Array<UserFcmTokenSchema>
}

export type UserGeolocation = {
  countryCode: string | null
  countryName: string | null
  continentName: string | null
  continentCode: string | null
}

export type UserLastPayout = {
  timestamp: string
  /**
   * 1 unit represents 1/10_000 cUSD
   */
  amount: number
}

export enum UserLevel {
  REGULAR = 'regular',
  VIP = 'vip'
}

export type UserMe = {
  haid: string | null
  /**
   * Latest HUID reported by current user client.
   */
  huid: string | null
  countryCode: string
  clientType: ClientType | null
  clientMajor: number | null
  celoWallet: string | null
  appInstanceId: number
}

export type UserMiniOMenuPointsState = {
  /**
   * Balance of user points
   */
  pointsBalance: number
  /**
   * Progress bar state.
   */
  progress: number
  pillText: string
  buttonLink: string
  /**
   * Amount in cUSD that user may receive when exchanging all points. 1 unit represents 1/10_000 cUSD.
   */
  earnedAmount: number | null
  readonly pointsBalanceFormatted: string
  readonly earnedAmountFormatted: string | null
}

export type UserMiniQuickaccessState = {
  /**
   * Total cUSD amount we paid out in rewards. 1 unit represents 1/10_000 cUSD
   */
  totalAmountGranted: number
  /**
   * Users' current streak length in the streaks mission.
   */
  streakLength: number
  /**
   * Indicates if user did check-in today
   */
  didCheckInToday: boolean
  /**
   * Users' current points balance. None means that points balance should not be shown.
   */
  pointsBalance: number | null
  /**
   * Roulette was removed. Kept as None to avoid breaking old clients.
   */
  rouletteUnusedSpinCount?: number | null
  readonly streakLengthFormatted: string
  readonly pointsBalanceFormatted: string | null
  readonly totalAmountGrantedFormatted: string
}

export type UserMissionSchema = {
  id: number
  appInstanceId: number | null
  missionType: MissionType
  clientType: string
  status: MissionStatus
  createdAt: string
  updatedAt: string | null
  entryPoint: string | null
  userClientId?: number | null
}

export type UserMissionsResponse = {
  items?: Array<UserMissionSchema>
}

export type ValidationError = {
  loc: Array<string | number>
  msg: string
  type: string
  input?: unknown
  ctx?: {
    [key: string]: unknown
  }
}

export type VoucherConfig = {
  claimSteps: Array<Translatable>
  claimUrl: string
}

export type VoucherData = {
  voucherType: VoucherType
  value: string
  /**
   * Datetime when the voucher expires. None means it never expires.
   */
  expiresAt?: string | null
  id: number
  config: VoucherConfig
}

/**
 * Represents all distinct voucher types available in the system.
 */
export enum VoucherType {
  SHOPEE_RS25 = 'shopee_rs25',
  CLARO_FREE_DATA = 'claro_free_data',
  SHOPEE_RS5 = 'shopee_rs5'
}

export type WalletAddressDailyRateLimitApiErrorBody = {
  detail: string
  errorCode: 'market_usdt_wallet_daily_limit_reached'
  extra: WalletRateLimitExtra
}

export type WalletAddressMinuteRateLimitApiErrorBody = {
  detail: string
  errorCode: 'market_usdt_wallet_minute_limit_reached'
  extra: WalletRateLimitExtra
}

export type WalletChallengeRequest = {
  /**
   * EIP-55 checksummed Celo wallet address that will sign the SIWE message.
   */
  walletAddress: string
}

export type WalletChallengeResponse = {
  /**
   * EIP-4361 SIWE message to be signed by the wallet via personal_sign. Valid for 5 minutes.
   */
  message: string
}

export type WalletRateLimitExtra = {
  limitKind: RateLimitKind
  limit: number
}

export type WallpaperAlreadyPurchasedApiErrorBody = {
  detail: string
  errorCode: 'market_wallpaper_already_purchased'
}

export type WallpaperItem = {
  /**
   * ID of wallpaper in https://gitlab.service.osa/ls/wallpaper-api/ project
   */
  id: number
  price: Amount
  state: MarketItemState
  offerHash: string
  /**
   * Additional offer-specific data (e.g., usdt_amount for USDT offers)
   */
  extra?: CamelModel | null
}

export type WallpaperOfferNotFoundApiErrorBody = {
  detail: string
  errorCode: 'market_wallpaper_offer_not_found'
}

export type WallpaperPurchaseRequest = {
  /**
   * ID of wallpaper in https://gitlab.service.osa/ls/wallpaper-api/ project
   */
  id: number
  offerHash: string
}

export type WallpaperPurchaseResponse = {
  /**
   * ID of wallpaper in https://gitlab.service.osa/ls/wallpaper-api/ project
   */
  id: number
  state: MarketItemState
}

export type WallpapersResponse = {
  items: Array<WallpaperItem>
}

export type InvalidateCacheForCurrentUserResponse = unknown

export type AnonymousLoginData = {
  /**
   * @deprecated
   */
  apiKey?: string | null
  requestBody: UserClientSchema
  xApiKey?: string | null
}

export type AnonymousLoginResponse = Tokens

export type TokensFromRefreshTokenData = {
  requestBody: TokenToRefresh
}

export type TokensFromRefreshTokenResponse = Tokens

export type WalletChallengeData = {
  requestBody: WalletChallengeRequest
}

export type WalletChallengeResponse2 = WalletChallengeResponse

export type BindWalletData = {
  requestBody: BindWalletRequest
}

export type BindWalletResponse = unknown

export type MeResponse = UserMe

export type ReportFcmTokenData = {
  requestBody: UserFcmToken
}

export type ReportFcmTokenResponse = unknown

export type MissionsResponse = MissionStatusesResponse

export type FeedbackData = {
  formData: Body_feedback
}

export type FeedbackResponse = unknown

export type GeolocationResponse = UserGeolocation

export type GetMiniQuickaccessStateResponse = UserMiniQuickaccessState

export type GetMiniOmenuPointsStateResponse = UserMiniOMenuPointsState

export type GetUserEarningsTotalResponse = UserEarningsTotal

export type UserConfigResponse = UserConfig

export type GetBalanceResponse = BalanceResponse

export type GetActivityData = {
  acceptLanguage?: string | null
  /**
   * Cursor for the next page
   */
  cursor?: string | null
  /**
   * Page size
   */
  size?: number
}

export type GetActivityResponse = CursorPage_ActivityEntry_

export type PostTelemetryEventsData = {
  requestBody: TelemetryEventRequest
}

export type PostTelemetryEventsResponse = unknown

export type ClaimBonusData = {
  campaignId: number
}

export type ClaimBonusResponse = CounterMissionExtra

export type GetMissionsV2Data = {
  acceptLanguage?: string | null
  xOperaRewardsEntrypoint?: string | null
}

export type GetMissionsV2Response = MissionsResponseV2

export type ReportCounterData = {
  acceptLanguage?: string | null
  forceSyncMode?: boolean
  missionType:
    | 'streaks'
    | 'set_wallpaper'
    | 'google_signin'
    | 'signin'
    | 'make_browser_default'
    | 'read_articles'
    | 'minipay_activate_subapp'
    | 'watch_ads'
  requestBody: CounterMissionReportPayload
  xOperaRewardsEntrypoint?: string | null
}

export type ReportCounterResponse = CounterMissionReportResponse

export type GetMissionHistoryData = {
  acceptLanguage?: string | null
  missionType: MissionType
  xOperaRewardsEntrypoint?: string | null
}

export type GetMissionHistoryResponse = MissionHistoryResponse

export type GetBalanceV2Response = BalanceV2Response

export type GetClientStateResponse = ClientStateResponse

export type PostClientStateData = {
  requestBody: ClientStateData
  xOperaRewardsEntrypoint?: string | null
}

export type PostClientStateResponse = unknown

export type GetMissionsV3Data = {
  acceptLanguage?: string | null
  xOperaRewardsEntrypoint?: string | null
}

export type GetMissionsV3Response = MissionsResponseV3

export type ReportCounterV3Data = {
  acceptLanguage?: string | null
  forceSyncMode?: boolean
  missionId: string
  requestBody: CounterMissionReportPayload
  xOperaRewardsEntrypoint?: string | null
}

export type ReportCounterV3Response = CounterMissionReportResponse

export type GetGiveawayAvailabilityData = {
  acceptLanguage?: string | null
}

export type GetGiveawayAvailabilityResponse = GiveawayStatus

export type PostFormGiveawayData = {
  requestBody: PayloadFormGiveaway
  xOperaRewardsEntrypoint?: string | null
}

export type PostFormGiveawayResponse = unknown

export type GetRewardStatusGiveawayData = {
  acceptLanguage?: string | null
  xOperaRewardsEntrypoint?: string | null
}

export type GetRewardStatusGiveawayResponse = ResponseRewardStatusGiveaway

export type GetFormData = {
  acceptLanguage?: string | null
  slug: CustomFormSlug
}

export type GetFormResponse = CustomFormConfig

export type PostSubmissionsData = {
  requestBody: CustomFormSubmissionRequest
  slug: CustomFormSlug
}

export type PostSubmissionsResponse = CustomFormSubmissionSchema

export type GetSubmissionsData = {
  slug: CustomFormSlug
}

export type GetSubmissionsResponse = CustomFormSubmissionsResponse

export type $OpenApiTs = {
  '/api/v1/debug/me/invalidate-cache': {
    post: {
      res: {
        /**
         * Successful Response
         */
        200: unknown
      }
    }
  }
  '/api/v1/auth/tokens': {
    post: {
      req: {
        /**
         * @deprecated
         */
        apiKey?: string | null
        requestBody: UserClientSchema
        xApiKey?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: Tokens
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/auth/tokens/refresh': {
    post: {
      req: {
        requestBody: TokenToRefresh
      }
      res: {
        /**
         * Successful Response
         */
        200: Tokens
        /**
         * These indicate potential client-side issues or implementation bugs.
         */
        400: ErrorDetail
        /**
         * The client should clear existing tokens and request a new anonymous user.
         */
        401: ErrorDetail
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/auth/wallet-challenge': {
    post: {
      req: {
        requestBody: WalletChallengeRequest
      }
      res: {
        /**
         * Successful Response
         */
        200: WalletChallengeResponse
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/auth/bind-wallet': {
    post: {
      req: {
        requestBody: BindWalletRequest
      }
      res: {
        /**
         * Successful Response
         */
        200: unknown
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/user/me': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: UserMe
      }
    }
  }
  '/api/v1/user/fcm-token': {
    post: {
      req: {
        requestBody: UserFcmToken
      }
      res: {
        /**
         * Successful Response
         */
        200: unknown
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/user/missions': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: MissionStatusesResponse
      }
    }
  }
  '/api/v1/user/feedback': {
    post: {
      req: {
        formData: Body_feedback
      }
      res: {
        /**
         * Successful Response
         */
        200: unknown
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/user/geolocation': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: UserGeolocation
      }
    }
  }
  '/api/v1/user/mini/quickaccess': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: UserMiniQuickaccessState
      }
    }
  }
  '/api/v1/user/mini/omenu/points/state': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: UserMiniOMenuPointsState
        /**
         * ApiError
         *
         * * Points not enabled for user
         */
        403: PointsNotEnabledForUserApiErrorBody
      }
    }
  }
  '/api/v1/user/earnings/total': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: UserEarningsTotal
      }
    }
  }
  '/api/v1/user/config': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: UserConfig
      }
    }
  }
  '/api/v1/user/balance': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: BalanceResponse
      }
    }
  }
  '/api/v1/user/activity': {
    get: {
      req: {
        acceptLanguage?: string | null
        /**
         * Cursor for the next page
         */
        cursor?: string | null
        /**
         * Page size
         */
        size?: number
      }
      res: {
        /**
         * Successful Response
         */
        200: CursorPage_ActivityEntry_
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/user/telemetry/events': {
    post: {
      req: {
        requestBody: TelemetryEventRequest
      }
      res: {
        /**
         * Successful Response
         */
        200: unknown
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/user/bonus-campaigns/{campaign_id}/claim': {
    post: {
      req: {
        campaignId: number
      }
      res: {
        /**
         * Successful Response
         */
        200: CounterMissionExtra
        /**
         * ApiError
         *
         * * No wallet address bound. A crypto token reward requires a wallet.
         */
        400: CryptoTokenNoWalletBoundApiErrorBody
        /**
         * ApiError
         *
         * * This bonus is no longer available. Please refresh your activity.
         */
        409: BonusClaimUnavailableApiErrorBody
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v2/user/missions': {
    get: {
      req: {
        acceptLanguage?: string | null
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: MissionsResponseV2
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v2/user/missions/{mission_type}/report-counter': {
    post: {
      req: {
        acceptLanguage?: string | null
        forceSyncMode?: boolean
        missionType:
          | 'streaks'
          | 'set_wallpaper'
          | 'google_signin'
          | 'signin'
          | 'make_browser_default'
          | 'read_articles'
          | 'minipay_activate_subapp'
          | 'watch_ads'
        requestBody: CounterMissionReportPayload
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: CounterMissionReportResponse
        /**
         * ApiError
         *
         * * User tried to get checkin endpoint but mission is not enabled for them
         *
         * * You can't check in again yet. Please try again later.
         *
         * * This action has already been completed.
         *
         * * We couldn't update your progress. Please try again.
         */
        400:
          | StreaksCheckInMissionIsNotEnabledForUserApiErrorBody
          | StreaksCheckInAttemptDuringActiveCoolOffApiErrorBody
          | CounterUpdateNothingToDoApiErrorBody
          | CounterUpdateFailedApiErrorBody
        /**
         * ApiError
         *
         * * Your app state appears to be out of date. Please refresh and try again.
         */
        409: CounterUpdateStatusConflictApiErrorBody
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v2/user/missions/{mission_type}/history': {
    get: {
      req: {
        acceptLanguage?: string | null
        missionType: MissionType
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: MissionHistoryResponse
        /**
         * ApiError
         *
         * * Mission history not enabled for user
         *
         * * Mission type does not support history
         */
        400:
          | MissionHistoryNotEnabledForUserApiErrorBody
          | MissionHistoryNotEnabledForMissionTypeApiErrorBody
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v2/user/balance': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: BalanceV2Response
      }
    }
  }
  '/api/v2/user/client-state': {
    get: {
      res: {
        /**
         * Successful Response
         */
        200: ClientStateResponse
      }
    }
    post: {
      req: {
        requestBody: ClientStateData
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: unknown
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v3/user/missions': {
    get: {
      req: {
        acceptLanguage?: string | null
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: MissionsResponseV3
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v3/user/missions/{mission_id}/report-counter': {
    post: {
      req: {
        acceptLanguage?: string | null
        forceSyncMode?: boolean
        missionId: string
        requestBody: CounterMissionReportPayload
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: CounterMissionReportResponse
        /**
         * ApiError
         *
         * * User tried to get checkin endpoint but mission is not enabled for them
         *
         * * You can't check in again yet. Please try again later.
         *
         * * This action has already been completed.
         *
         * * We couldn't update your progress. Please try again.
         */
        400:
          | StreaksCheckInMissionIsNotEnabledForUserApiErrorBody
          | StreaksCheckInAttemptDuringActiveCoolOffApiErrorBody
          | CounterUpdateNothingToDoApiErrorBody
          | CounterUpdateFailedApiErrorBody
        /**
         * ApiError
         *
         * * Your app state appears to be out of date. Please refresh and try again.
         */
        409: CounterUpdateStatusConflictApiErrorBody
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/giveaway/availability': {
    get: {
      req: {
        acceptLanguage?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: GiveawayStatus
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/giveaway/form': {
    post: {
      req: {
        requestBody: PayloadFormGiveaway
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: unknown
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/giveaway/reward-status': {
    get: {
      req: {
        acceptLanguage?: string | null
        xOperaRewardsEntrypoint?: string | null
      }
      res: {
        /**
         * Successful Response
         */
        200: ResponseRewardStatusGiveaway
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/forms/{slug}': {
    get: {
      req: {
        acceptLanguage?: string | null
        slug: CustomFormSlug
      }
      res: {
        /**
         * Successful Response
         */
        200: CustomFormConfig
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
  '/api/v1/forms/{slug}/submissions': {
    post: {
      req: {
        requestBody: CustomFormSubmissionRequest
        slug: CustomFormSlug
      }
      res: {
        /**
         * Successful Response
         */
        200: CustomFormSubmissionSchema
        /**
         * ApiError
         *
         * * Form submission limit reached
         */
        409: CustomFormMaxSubmissionsReachedApiErrorBody
        /**
         * ApiError
         *
         * * Provided values are invalid
         */
        422: CustomFormValidationApiErrorBody
      }
    }
    get: {
      req: {
        slug: CustomFormSlug
      }
      res: {
        /**
         * Successful Response
         */
        200: CustomFormSubmissionsResponse
        /**
         * Validation Error
         */
        422: HTTPValidationError
      }
    }
  }
}
